/* eslint-disable */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useAppSettings, resolveLogo } from "../lib/AppSettingsContext";import { useTheme, syncThemeFromUser } from "../lib/ThemeContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Shared library
import {
  API_BASE,
  kcFetch,
  getToken,
  setToken,
  authHeaders,
  _apiGet,
  _apiPost,
  _apiPut,
  _apiDelete,
} from "../lib/apiClient";
import { setViewer } from "../lib/viewerContext";
import { setMembers as setMembersContext } from "../lib/membersContext";
import { fullName, initials, formatDate, age, uid, upcomingBirthdays, sortMembersByName, sortUsersByName } from "../lib/format";
import { Avatar } from "./shared/Avatar";
import { CoverImage } from "./shared/CoverImage";
import CardPreview from "./shared/CardPreview";
import { DarkModePopup } from "./shared/DarkModePopup";
import { PhotoUploader } from "./shared/PhotoUploader";
// Departments feature — extracted into /components/departments/
import {
  DepartmentFormModal,
  DepartmentDetailPage,
  DepartmentWidget,
  apiListDepartments,
  apiCreateDepartment,
  apiUpdateDepartment,
  apiDeleteDepartment,
  apiListPositions,
} from "./departments";
// Tasks / Appointments / Follow-Ups feature folders — extracted post-Phase-3.
import { TasksPage } from "./tasks";
import { AppointmentsPage, PublicAppointmentBookingPage } from "./appointments";import { FollowUpsPage } from "./followups";
import { FollowUpTreePage } from "./followuptree";
import LogEntryPage from "./followuptree/LogEntryPage";
// Dashboard / Members / Attendance / Events / Reports — Wave 3 extraction.
import { DashboardPage } from "./dashboard";
import BirthdaysThisWeekPage from "./dashboard/BirthdaysThisWeekPage";
import MyAttendancePage from "./dashboard/MyAttendancePage";
import { MembersPage } from "./members";
import { AttendancePage } from "./attendance";
import { EventsPage } from "./events";
import { ReportsPage, BaptismReport, NewMembersReport, FamilyGrowthReport } from "./reports";
import AnalyticsPage from "./analytics";
import { DonationsPage } from "./donations";
import { FinancePage } from "./finance";
// Calendar (Point 7) — extracted dual-view advanced calendar.
import FullCalendarPageRoute from "./calendar/FullCalendarPageRoute";
import BlockoutFormModal from "./calendar/BlockoutFormModal";
import { VisitorWelcomePage } from "./visitor";
import FoodFormPublicPage from "./donations/FoodFormPublicPage";
import PublicDonationsListingsPage from "./donations/PublicDonationsListingsPage";
import PublicEventRegisterPage from "./events/PublicEventRegisterPage";
import { QRPosterPage } from "./qrposter";
import { ServicesPage } from "./services";
import { MyProfilePage } from "./profile";
import { FamiliesPage } from "./families";
import { UsersPage, BulkImportPage, SettingsPage } from "./admin";
import { NotificationsArchivePage } from "./notifications";
import { GivingPage } from "./giving";
import NotifPanel from "./NotifPanel";
import SwipeableNotif from "./SwipeableNotif";
import ComponentPreviewPage from "./ComponentPreviewPage";
import PastorsDirectoryPage from "./admin/PastorsDirectoryPage";
import { NOTIF_SECTIONS, NOTIF_OTHER_SECTION, notifSectionFor } from "../lib/notifSections";
import { MonthGrid as CalMonthGrid, buildCalendarItems as buildCalItemsExt, ymdKey as ymdKeyExt, sameYMD as sameYMDExt, startOfDay as startOfDayExt, expandServiceOccurrences as expandSvcOccExt } from "./calendar/helpers";
import { CustomIcon } from "./shared/CustomIcon";

// ============================================================
// ROUTE MAP — single source of truth for navigation + RBAC
// ============================================================
const NAV_ITEMS = [
  // ---- MAIN: Phase-2 sidebar order — Home, The Flock, Tasks, Browse, Notifications, Calendar.
  { section:"Main",    id:"dashboard",     customIcon:"dashboard",        label:"Home",           path:"/dashboard",            roles:["superadmin","limited_admin","pastor","team_member"] },
  { section:"Main",    id:"members",       customIcon:"people",           label:"The Flock",      path:"/members",              roles:["superadmin","limited_admin","pastor","team_member"] },
  { section:"Main",    id:"tasks",         customIcon:"check",            label:"Tasks",          path:"/tasks",                roles:["superadmin","limited_admin","pastor","team_member"] },
  { section:"Main",    id:"browse",        customIcon:"browse",           label:"Browse",         path:"/browse",               roles:["superadmin","limited_admin","pastor","team_member"] },
  { section:"Main",    id:"notifications", customIcon:"bell",             label:"Notifications",  path:"/notifications",        roles:["superadmin","limited_admin","pastor","team_member"] },
  { section:"Main",    id:"calendar",      customIcon:"calendar",         label:"Calendar",       path:"/check-ins",            roles:["superadmin","limited_admin","pastor","team_member"] },
  // ---- MAIN (sidebar-hidden) — routable + surfaced via Browse only
  { section:"Main",    id:"followups",     customIcon:"chat",             label:"Follow-Ups",     path:"/followuptree",           roles:["superadmin","limited_admin","pastor","team_member"], hideFromSidebar:true },
  { section:"Main",    id:"appointments",  customIcon:"calendar-clock",   label:"Appointments",   path:"/appointments",         roles:["superadmin","limited_admin","pastor","team_member"], hideFromSidebar:true },
  { section:"Main",    id:"families",      customIcon:"family",           label:"Families",       path:"/families",             roles:["superadmin","limited_admin","pastor","team_member"], hideFromSidebar:true },
  { section:"Main",    id:"attendance",    customIcon:"clipboard",        label:"Attendance",     path:"/attendance",           roles:["superadmin","limited_admin","pastor","team_member"], hideFromSidebar:true },
  { section:"Main",    id:"events",        customIcon:"event-ticket",     label:"Events",         path:"/events",               roles:["superadmin","limited_admin","pastor","team_member"], hideFromSidebar:true },
  { section:"Main",    id:"birthdays",     customIcon:"birthday-cake",    label:"Birthdays",      path:"/birthdays",            roles:["superadmin","limited_admin","team_member"], hideFromSidebar:true },
  { section:"Reports", id:"reports",       customIcon:"chart",            label:"Analytics",      path:"/reports/analytics",    roles:["superadmin","limited_admin","pastor"] },
  { section:"Reports", id:"baptism_report",customIcon:"baptism",          label:"Baptism Report", path:"/reports/baptism",      roles:["superadmin","limited_admin"], hideFromSidebar:true },
  { section:"Reports", id:"members_report",customIcon:"person",           label:"New Members",    path:"/reports/new-members",  roles:["superadmin","limited_admin"], hideFromSidebar:true },
  { section:"Reports", id:"family_report", customIcon:"chart",            label:"Family Growth",  path:"/reports/family-growth",roles:["superadmin","limited_admin"], hideFromSidebar:true },
  { section:"Reports", id:"audit",         customIcon:"lock",             label:"Audit Log",      path:"/audit",                roles:["superadmin","limited_admin"] },
  { section:"Admin",   id:"users",         customIcon:"person-settings",  label:"Users & Roles",  path:"/admin/users",          roles:["superadmin"] },
  { section:"Admin",   id:"pastors_dir",   customIcon:"church",           label:"Pastors Directory", path:"/admin/pastors-directory", roles:["superadmin","limited_admin"] },
  { section:"Admin",   id:"services",      customIcon:"dove",             label:"Services",       path:"/admin/services",       roles:["superadmin"] },
  { section:"Admin",   id:"bulk_import",   customIcon:"download-inbox",   label:"Bulk Import",    path:"/admin/bulk-import",    roles:["superadmin"] },
  { section:"Admin",   id:"settings",      customIcon:"tools",            label:"Settings",        path:"/admin/settings",       roles:["superadmin"] },
  { section:"Admin",   id:"qr_poster",     customIcon:"qr",               label:"QR Poster",      path:"/admin/qr-poster",      roles:["superadmin","limited_admin","pastor","team_member"], hideFromSidebar:true },
];

function userCanAccess(role, requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  return requiredRoles.includes(role);
}

function buildNavGroups(role) {
  const groups = [];
  const sections = ["Main", "Reports", "Admin"];
  for (const s of sections) {
    const items = NAV_ITEMS.filter(i => i.section === s && !i.hideFromSidebar && userCanAccess(role, i.roles));
    if (items.length) groups.push({ section: s, items });
  }
  return groups;
}

// ============================================================
// STYLES — Kingdom Connect Brand
// ============================================================
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --gold: var(--kc-accent);
    --gold2: #e8c84a;
    --charcoal: var(--kc-text);
    --charcoal2: var(--kc-elevated);
    --cream: var(--kc-surface);
    --cream2: color-mix(in srgb, var(--kc-accent) 15%, transparent);
    --white: var(--kc-elevated);
    --text: var(--kc-text);
    --text2: var(--kc-sub);
    --text3: var(--kc-meta);
    --green: #4ade80;
    --green2: rgba(45,122,79,0.25);
    --red: #ff6b6b;
    --red2: rgba(184,50,50,0.20);
    --blue: #60a5fa;
    --blue2: rgba(37,99,235,0.20);
    --shadow: 0 4px 24px rgba(0,0,0,0.5);
    --shadow2: 0 2px 12px rgba(0,0,0,0.4);
    --gold-line: color-mix(in srgb, var(--kc-accent) 15%, transparent);
    --gold-line-2: color-mix(in srgb, var(--kc-accent) 25%, transparent);
    --gold-line-3: color-mix(in srgb, var(--kc-accent) 40%, transparent);
    --row-divider: rgba(255,255,255,0.06);
    --radius: 12px;
    --radius2: 8px;
  }

  html, body, #root { background: var(--kc-bg); }
  body { font-family: 'Inter', 'Montserrat', sans-serif; background: var(--kc-bg); color: var(--kc-text); min-height: 100vh; }

  /* LOGIN */
  .login-bg {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: var(--kc-bg);
    padding: 24px; position: relative; overflow: hidden;
  }
  .login-bg::before {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse at 10% 20%, color-mix(in srgb, var(--kc-accent) 8%, transparent) 0%, transparent 55%),
                radial-gradient(ellipse at 90% 80%, color-mix(in srgb, var(--kc-accent) 6%, transparent) 0%, transparent 50%);
  }
  .login-card {
    background: var(--kc-elevated); border-radius: 20px; padding: 44px 40px;
    width: 100%; max-width: 440px; box-shadow: 0 24px 80px rgba(0,0,0,0.5);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    position: relative; z-index: 1;
  }
  .login-logo-img {
    display: block; width: 180px; height: auto; margin: 0 auto 18px;
    border-radius: 12px;
  }
  .login-title { font-family: 'Cinzel', serif; font-size: 24px; font-weight: 700; color: var(--kc-text); text-align: center; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.06em; }
  .login-tagline { font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-accent); text-align: center; margin-bottom: 28px; font-style: italic; font-weight: 500; letter-spacing: 0.06em; text-transform: uppercase; }
  .login-label { font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; color: var(--kc-sub); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px; display: block; }
  .login-input {
    width: 100%; padding: 12px 16px; border: 1.5px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 8px;
    font-size: 14px; font-family: 'Montserrat', sans-serif; background: var(--kc-surface);
    outline: none; transition: border 0.2s, box-shadow 0.2s; margin-bottom: 14px; color: var(--kc-text);
  }
  .login-input::placeholder { color: var(--kc-meta); }
  .login-input:focus { border-color: var(--kc-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--kc-accent) 12%, transparent); }
  .login-btn {
    width: 100%; padding: 14px; background: var(--kc-accent);
    color: var(--kc-on-accent); border: none; border-radius: 8px; font-size: 14px;
    font-weight: 700; font-family: 'Montserrat', sans-serif; cursor: pointer; transition: opacity 0.2s, transform 0.1s; margin-top: 6px;
    text-transform: uppercase; letter-spacing: 0.08em;
  }
  .login-btn:hover { opacity: 0.92; transform: translateY(-1px); }
  .login-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .login-error { background: rgba(184,50,50,0.20); color: #ff6b6b; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 14px; font-family: 'Montserrat', sans-serif; border: 1px solid rgba(184,50,50,0.30); }
  .login-success { background: rgba(45,122,79,0.25); color: #4ade80; padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 14px; font-family: 'Montserrat', sans-serif; border: 1px solid rgba(45,122,79,0.30); }
  .login-link { font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-accent); text-align: center; cursor: pointer; margin-top: 14px; text-decoration: underline; }
  .session-banner { background: var(--kc-accent); color: var(--kc-on-accent); text-align: center; padding: 8px; font-size: 12px; font-weight: 700; font-family: 'Montserrat', sans-serif; }

  /* LAYOUT */
  .layout { display: flex; min-height: 100vh; background: var(--kc-bg); position: relative; }

  /* SIDEBAR */
  .sidebar {
    width: 248px; min-height: 100vh; background: var(--kc-surface);
    display: flex; flex-direction: column; flex-shrink: 0;
    position: sticky; top: 0; height: 100vh; overflow-y: auto;
    border-right: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
  }
  /* Dark-mode sidebar: sit flush on page bg (#111111) — page + sidebar
     share the same charcoal so the border-right becomes the only
     divider. This is the Phase-4 identity. */
  .dark .sidebar { background: var(--kc-dark-bg); border-right-color: var(--kc-dark-border); }
  .dark .nav-item.active { background: var(--kc-dark-accent-bg); color: var(--kc-dark-accent); border-left-color: var(--kc-dark-accent); }
  .sidebar-logo {
    padding: 20px 16px 16px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
    display: flex; flex-direction: column; align-items: center; text-align: center;
  }
  .sidebar-logo-img {
    width: 140px; height: auto; display: block; margin-bottom: 6px; border-radius: 8px;
  }
  .sidebar-logo-sub { font-family: 'Montserrat', sans-serif; font-size: 10px; color: color-mix(in srgb, var(--kc-accent) 60%, transparent); margin-top: 2px; font-style: italic; letter-spacing: 0.06em; line-height: 1.4; text-transform: uppercase; }
  .sidebar-nav { flex: 1; padding: 14px 10px; }
  .sidebar-section { font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: var(--kc-meta); padding: 14px 10px 6px; }
  .nav-item {
    display: flex; align-items: center; gap: 10px; padding: 9px 12px;
    border-radius: 8px; cursor: pointer; transition: all 0.15s;
    color: var(--kc-sub); font-size: 13px; font-weight: 400; margin-bottom: 2px;
    border: none; background: none; width: 100%; text-align: left;
    font-family: 'Montserrat', sans-serif; border-left: 3px solid transparent;
    text-decoration: none;
  }
  a.nav-item, a.nav-item:visited { color: var(--kc-sub); text-decoration: none; }
  .nav-item:hover { background: rgba(255,255,255,0.06); color: var(--kc-text); }
  .nav-item.active { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); color: var(--kc-accent); font-weight: 600; border-left-color: var(--kc-accent); }
  .nav-item .nav-icon { font-size: 15px; width: 20px; text-align: center; display: inline-flex; align-items: center; justify-content: center; }
  .nav-item .nav-icon img.kc-custom-icon { filter: drop-shadow(0 0 4px color-mix(in srgb, var(--kc-accent) 18%, transparent)); }
  .nav-item.active .nav-icon img.kc-custom-icon { filter: drop-shadow(0 0 6px color-mix(in srgb, var(--kc-accent) 55%, transparent)); }
  .sidebar-footer { padding: 14px 12px; border-top: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); }
  .sidebar-avatar {
    width: 32px; height: 32px; border-radius: 50%; background: var(--kc-accent-grad);
    display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: var(--kc-on-accent); flex-shrink: 0;
  }
  .sidebar-user-name { font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 500; color: var(--kc-text); }
  .sidebar-user-role { font-family: 'Montserrat', sans-serif; font-size: 10px; color: var(--kc-meta); }
  .logout-btn {
    width: 100%; padding: 8px 12px; background: rgba(255,255,255,0.04); border: none;
    border-radius: 8px; color: var(--kc-meta); font-size: 12px;
    cursor: pointer; transition: all 0.15s; margin-top: 8px; text-align: left;
    font-family: 'Montserrat', sans-serif;
  }
  .logout-btn:hover { background: rgba(255,255,255,0.08); color: var(--kc-text); }

  /* ================================================================
     PHASE-2 SIDEBAR MODE SYSTEM
     Three modes controlled via [data-sidebar-mode] on .layout:
       expanded (240 px)  | collapsed (64 px, icons + tooltips)
       glass    (0 px push, 4 px strip, hover reveals overlay panel)
     Desktop-only — mobile (≤ 768 px) hides the whole sidebar and
     shows BottomNavBar via existing @media rule.
     ================================================================ */
  .sidebar {
    width: 240px;
    transition: width 0.25s ease;
    z-index: 20;
  }
  .layout[data-sidebar-mode="expanded"]  .sidebar { width: 240px; }
  .layout[data-sidebar-mode="collapsed"] .sidebar { width: 64px; }
  .layout[data-sidebar-mode="glass"]     .sidebar {
    width: 0;
    overflow: visible;
    position: fixed;
    top: 0; left: 0; height: 100vh;
    background: transparent;
    border-right: none;
    pointer-events: none;
  }
  /* Glass mode: hover the strip OR the sidebar reveals a frosted
     overlay panel that does NOT push page content. */
  .layout[data-sidebar-mode="glass"] .sidebar:hover,
  .layout[data-sidebar-mode="glass"] .sidebar-glass-strip:hover ~ .sidebar {
    width: 240px;
    background: rgba(10,10,10,0.90);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    border-right: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    pointer-events: auto;
    transform: translateX(0);
    transition: width 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  body.theme-light .layout[data-sidebar-mode="glass"] .sidebar:hover,
  body.theme-light .layout[data-sidebar-mode="glass"] .sidebar-glass-strip:hover ~ .sidebar {
    background: rgba(255,255,255,0.88);
  }
  .layout[data-sidebar-mode="glass"] .sidebar {
    transform: translateX(-40px);
    transition: width 0.2s ease, transform 0.2s ease;
  }
  /* Content fade — hide labels/sections/user-chip in collapsed + glass-rest */
  .sidebar-fade { opacity: 1; transition: opacity 0.1s ease 0.15s; }
  .layout[data-sidebar-mode="collapsed"] .sidebar-fade,
  .layout[data-sidebar-mode="glass"]     .sidebar-fade {
    opacity: 0;
    transition: opacity 0.1s ease;
    pointer-events: none;
  }
  .layout[data-sidebar-mode="glass"] .sidebar:hover .sidebar-fade,
  .layout[data-sidebar-mode="glass"] .sidebar-glass-strip:hover ~ .sidebar .sidebar-fade {
    opacity: 1;
    transition: opacity 0.1s ease 0.15s;
    pointer-events: auto;
  }
  /* Logo shrinks to 32 px in collapsed mode */
  .layout[data-sidebar-mode="collapsed"] .sidebar-logo { padding: 12px 8px; }
  .layout[data-sidebar-mode="collapsed"] .sidebar-logo-img { width: 32px; margin-bottom: 0; }

  /* Sidebar sections: hide the label but keep vertical spacing so
     icons don't clump together in collapsed mode. */
  .layout[data-sidebar-mode="collapsed"] .sidebar-section,
  .layout[data-sidebar-mode="glass"]     .sidebar-section {
    height: 12px; padding: 6px 0; overflow: hidden;
  }

  /* Nav item: keep icon centred when collapsed */
  .nav-item { transition: background 0.15s, color 0.15s, border-color 0.15s; position: relative; }
  .nav-item-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .layout[data-sidebar-mode="collapsed"] .nav-item,
  .layout[data-sidebar-mode="glass"] .sidebar:not(:hover) .nav-item {
    justify-content: center;
    padding: 10px 8px;
    gap: 0;
  }
  .layout[data-sidebar-mode="collapsed"] .nav-item .nav-item-label,
  .layout[data-sidebar-mode="glass"] .sidebar:not(:hover) .nav-item .nav-item-label {
    display: none;
  }

  /* Active-state right dot */
  .nav-item.active::after {
    content: '';
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--kc-accent);
    box-shadow: 0 0 6px var(--kc-accent);
    opacity: 1;
    transition: opacity 0.15s;
  }
  .layout[data-sidebar-mode="collapsed"] .nav-item.active::after,
  .layout[data-sidebar-mode="glass"] .sidebar:not(:hover) .nav-item.active::after {
    right: 6px;
  }

  /* Active nav-item uses token accent (indigo light / gold dark) */
  .nav-item.active { background: var(--kc-accent-bg); color: var(--kc-accent); border-left-color: var(--kc-accent); }

  /* Tooltip on hover when collapsed / glass-rest */
  .nav-item[data-tooltip]::before {
    content: attr(data-tooltip);
    position: absolute;
    left: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%);
    background: var(--kc-surface);
    color: var(--kc-text);
    padding: 6px 10px;
    border-radius: 6px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0,0,0,0.20);
    transition: opacity 0.15s;
    z-index: 40;
  }
  .layout[data-sidebar-mode="collapsed"] .nav-item[data-tooltip]:hover::before {
    opacity: 1;
  }

  /* Sidebar user chip (footer) */
  .sidebar-user-chip {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 10px;
    background: var(--kc-accent-bg);
    color: var(--kc-text);
    transition: background 0.15s;
  }
  .sidebar-user-chip:hover { background: var(--kc-elevated); }
  .layout[data-sidebar-mode="collapsed"] .sidebar-user-chip,
  .layout[data-sidebar-mode="glass"] .sidebar:not(:hover) .sidebar-user-chip {
    display: none;
  }

  /* Sidebar logout (always red text; icon-only when collapsed) */
  .sidebar-logout {
    display: flex; align-items: center; gap: 10px;
    width: 100%; padding: 10px 12px; margin-top: 8px;
    border: none; background: none; border-radius: 8px;
    color: #EF4444; font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
    cursor: pointer; text-align: left;
  }
  .sidebar-logout:hover { background: rgba(239,68,68,0.10); }
  .layout[data-sidebar-mode="collapsed"] .sidebar-logout,
  .layout[data-sidebar-mode="glass"] .sidebar:not(:hover) .sidebar-logout {
    justify-content: center;
    padding: 10px 8px;
    gap: 0;
  }
  .layout[data-sidebar-mode="collapsed"] .sidebar-logout .nav-item-label,
  .layout[data-sidebar-mode="glass"] .sidebar:not(:hover) .sidebar-logout .nav-item-label {
    display: none;
  }

  /* Glass strip — 4 px vertical bar on left edge. Only visible in
     glass mode; tappable to cycle the sidebar mode. */
  .sidebar-glass-strip {
    position: fixed;
    top: 0;
    left: 0;
    width: 4px;
    height: 100vh;
    background: var(--kc-accent);
    z-index: 15;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s, width 0.15s;
  }
  .layout[data-sidebar-mode="glass"] .sidebar-glass-strip {
    opacity: 1;
    pointer-events: auto;
  }
  .layout[data-sidebar-mode="glass"] .sidebar-glass-strip:hover { width: 6px; }

  /* Sidebar chevron toggle — pinned to the right edge of the sidebar,
     vertically centered. Sits half-inside / half-outside the sidebar so
     it stays discoverable in all three modes. Cycles expanded → collapsed
     → glass on click. Hidden on mobile (sidebar is hidden below 768 px). */
  .sidebar-toggle {
    position: fixed;
    top: 50%;
    /* Default position — updated per data-sidebar-mode below */
    left: 234px;
    transform: translateY(-50%);
    width: 28px; height: 28px;
    display: inline-flex; align-items: center; justify-content: center;
    border: 1px solid var(--kc-border);
    background: var(--kc-elevated); color: var(--kc-sub);
    border-radius: 50%;
    cursor: pointer; flex-shrink: 0;
    box-shadow: var(--kc-shadow);
    transition: left 0.25s ease, background 0.15s, color 0.15s, transform 0.15s;
    z-index: 30;
  }
  .layout[data-sidebar-mode="expanded"]  .sidebar-toggle { left: 234px; }  /* 248 - 14 */
  .layout[data-sidebar-mode="collapsed"] .sidebar-toggle { left: 50px;  }  /* 64  - 14 */
  .layout[data-sidebar-mode="glass"]     .sidebar-toggle { left: -10px; }  /* on the glass strip */
  .sidebar-toggle:hover { background: color-mix(in srgb, var(--kc-accent) 12%, var(--kc-elevated)); color: var(--kc-accent); }
  .sidebar-toggle:active { transform: translateY(-50%) scale(0.94); }

  @media (max-width: 768px) {
    /* Toggle + glass strip are desktop-only */
    .sidebar-toggle { display: none; }
    .sidebar-glass-strip { display: none; }
  }
  /* ================================================================ */

  /* MAIN */
  .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: var(--kc-bg); }
  .topbar {
    background: var(--kc-surface); border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); padding: 14px 28px;
    display: flex; align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 10;
  }
  .topbar-title { font-family: 'Cinzel', serif; font-size: 20px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em; }
  .topbar-right { display: flex; align-items: center; gap: 12px; position: relative; }
  .notif-btn {
    width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.06);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); cursor: pointer; display: flex; align-items: center; justify-content: center;
    font-size: 16px; position: relative; transition: background 0.15s; color: var(--kc-text);
  }
  .notif-btn:hover { background: rgba(255,255,255,0.10); }
  .notif-badge {
    position: absolute; top: 4px; right: 4px; width: 8px; height: 8px;
    background: var(--kc-accent); border-radius: 50%; border: 2px solid #1A1A1A;
  }

  .theme-toggle-btn {
    width: 36px; height: 36px; border-radius: 50%;
    border: 1px solid var(--kc-border);
    background: var(--kc-elevated); color: var(--kc-sub);
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .theme-toggle-btn:hover { background: var(--kc-border); color: var(--kc-text); }
  .dark .theme-toggle-btn {
    border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent);
    color: var(--kc-dark-accent);
    background: color-mix(in srgb, var(--kc-accent) 5%, transparent);
  }
  .dark .theme-toggle-btn:hover { background: color-mix(in srgb, var(--kc-accent) 12%, transparent); }
  @media (max-width: 768px) {
    .theme-toggle-btn { width: 34px; height: 34px; }
  }
  .content { flex: 1; padding: 26px; overflow-y: auto; background: var(--kc-bg); color: var(--kc-text); }

  /* CARDS */
  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px; margin-bottom: 22px; }
  .stat-card {
    background: var(--kc-elevated); border-radius: 12px; padding: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.4); border: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
  }
  .stat-icon { font-size: 22px; margin-bottom: 10px; color: var(--kc-accent); }
  .stat-value { font-family: 'Cinzel', serif; font-size: 32px; font-weight: 700; color: var(--kc-text); line-height: 1; }
  .stat-label { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-meta); margin-top: 6px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
  .stat-trend { font-family: 'Montserrat', sans-serif; font-size: 11px; color: #4ade80; margin-top: 8px; }

  .card { background: var(--kc-surface); border-radius: var(--kc-radius); box-shadow: var(--kc-shadow); border: 1px solid var(--kc-border); color: var(--kc-text); }
  .card-header { padding: 16px 20px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
  .card-title { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em; }
  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  th { padding: 11px 16px; text-align: left; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--kc-meta); background: var(--kc-elevated); border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); }
  td { padding: 12px 16px; font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-text); border-bottom: 1px solid rgba(255,255,255,0.06); vertical-align: middle; }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: color-mix(in srgb, var(--kc-accent) 5%, transparent); }
  tbody tr:nth-child(odd) td { background: rgba(255,255,255,0.015); }
  tbody tr:nth-child(odd):hover td { background: color-mix(in srgb, var(--kc-accent) 5%, transparent); }

  /* BADGES */
  .badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .badge-green { background: rgba(45,122,79,0.25); color: #4ade80; }
  .badge-red { background: rgba(184,50,50,0.20); color: #ff6b6b; }
  .badge-gold { background: color-mix(in srgb, var(--kc-accent) 15%, transparent); color: var(--kc-accent); }
  .badge-charcoal { background: rgba(255,255,255,0.08); color: var(--kc-text); }
  .badge-blue { background: rgba(37,99,235,0.20); color: #60a5fa; }
  .badge-gray { background: rgba(255,255,255,0.08); color: var(--kc-meta); }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 8px; font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s; border: none; text-transform: uppercase; letter-spacing: 0.06em; }
  .btn.btn-primary { background: var(--kc-accent); color: var(--kc-on-accent); }
  .btn.btn-primary:hover { opacity: 0.9; }
  .btn-gold { background: var(--kc-accent); color: var(--kc-on-accent); font-weight: 700; }
  .btn-gold:hover { opacity: 0.9; }
  .btn-outline { background: transparent; color: var(--kc-text); border: 1.5px solid color-mix(in srgb, var(--kc-accent) 40%, transparent); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
  .btn-outline:hover { border-color: var(--kc-accent); color: var(--kc-accent); }
  .btn-danger { background: rgba(184,50,50,0.20); color: #ff6b6b; border: 1px solid rgba(184,50,50,0.30); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
  .btn-danger:hover { background: rgba(184,50,50,0.30); }
  .btn-sm { padding: 6px 11px; font-size: 11px; }
  .btn-icon { width: 32px; height: 32px; padding: 0; justify-content: center; }

  /* FORMS */
  .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 14px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group.full { grid-column: 1 / -1; }
  label { font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; color: var(--kc-sub); text-transform: uppercase; letter-spacing: 0.06em; }
  input, select, textarea {
    padding: 11px 14px; border: 1.5px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 8px;
    font-size: 13px; font-family: 'Montserrat', sans-serif; background: var(--kc-elevated);
    outline: none; transition: border 0.2s, box-shadow 0.2s; color: var(--kc-text); width: 100%;
  }
  input::placeholder, textarea::placeholder { color: var(--kc-meta); }
  input:focus, select:focus, textarea:focus { border-color: var(--kc-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--kc-accent) 12%, transparent); }
  select option { background: var(--kc-elevated); color: var(--kc-text); }
  textarea { resize: vertical; min-height: 84px; }
  .form-section-title { font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700; color: var(--kc-accent); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 2px solid color-mix(in srgb, var(--kc-accent) 40%, transparent); display: inline-block; }
  input[type="checkbox"], input[type="radio"] { width: auto; accent-color: var(--kc-accent); }

  /* MODAL */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 10000;
    display: flex; align-items: center; justify-content: center; padding: 20px;
    backdrop-filter: blur(4px);
  }
  /* On mobile, reserve room above the bottom navigation bar so modal footers
     are never hidden behind it. The bottom-nav is 64px + safe-area. */
  @media (max-width: 768px) {
    .modal-overlay {
      padding-bottom: calc(20px + 64px + env(safe-area-inset-bottom, 0px));
      align-items: flex-end;
    }
    .modal { max-height: calc(100vh - 84px - env(safe-area-inset-bottom, 0px)); }
  }
  .modal {
    background: var(--kc-elevated); border-radius: 16px; width: 100%; max-width: 720px;
    max-height: 85vh; overflow: hidden;
    display: flex; flex-direction: column;
    box-shadow: 0 32px 80px rgba(0,0,0,0.6);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); color: var(--kc-text);
  }
  .modal-header {
    padding: 20px 24px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
    display: flex; align-items: center; justify-content: space-between;
    background: var(--kc-elevated); border-radius: 16px 16px 0 0;
    flex-shrink: 0;
  }
  .modal-title { font-family: 'Cinzel', serif; font-size: 18px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em; }
  .modal-close { width: 32px; height: 32px; border-radius: 50%; border: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); background: rgba(255,255,255,0.04); cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center; color: var(--kc-text); }
  .modal-close:hover { background: rgba(255,255,255,0.10); border-color: var(--kc-accent); }
  .modal-body {
    padding: 24px; color: var(--kc-text);
    flex: 1 1 auto; overflow-y: auto;
    /* Bottom padding accounts for iOS safe-area + the in-app bottom navigation bar */
    padding-bottom: calc(24px + env(safe-area-inset-bottom));
  }
  .modal-footer {
    padding: 14px 22px;
    padding-bottom: calc(14px + env(safe-area-inset-bottom));
    border-top: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
    display: flex; justify-content: flex-end; gap: 10px;
    background: var(--kc-elevated);
    flex-shrink: 0;
    /* Stay above the in-app bottom navigation bar on mobile */
    position: relative; z-index: 2;
  }

  /* Global mobile clearance for any fixed/absolute panel or modal that
     appears near the bottom edge. The 64px accounts for the bottom
     navigation bar height. */
  .mobile-safe-bottom-pad {
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  }
  @media (max-width: 768px) {
    .mobile-safe-bottom-pad {
      padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
    }
    /* Detail panels that slide up from the bottom on mobile */
    [data-testid$="-panel"][data-mobile-sheet="true"],
    .mobile-sheet {
      padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important;
    }
  }

  /* SEARCH */
  .search-bar { display: flex; align-items: center; gap: 8px; background: var(--kc-elevated); border: 1.5px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 8px; padding: 8px 14px; flex: 1; max-width: 320px; color: var(--kc-text); }
  .search-bar input { border: none; background: transparent; padding: 0; font-size: 13px; outline: none; width: 100%; color: var(--kc-text); }
  .search-bar input::placeholder { color: var(--kc-meta); }
  .search-bar:focus-within { border-color: var(--kc-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--kc-accent) 12%, transparent); }

  /* TABS */
  .tabs { display: flex; gap: 4px; border-bottom: 2px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); margin-bottom: 18px; flex-wrap: wrap; }
  .tab { padding: 10px 18px; font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 500; cursor: pointer; border: none; background: none; color: var(--kc-meta); border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s; text-transform: uppercase; letter-spacing: 0.05em; }
  .tab.active { color: var(--kc-text); border-bottom-color: var(--kc-accent); font-weight: 700; }
  .tab:hover:not(.active) { color: var(--kc-sub); }

  /* AVATAR */
  .avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 700; color: var(--kc-on-accent); flex-shrink: 0; background: var(--kc-accent-grad); }

  /* PROFILE */
  .profile-hero { background: linear-gradient(135deg, #2a2a2a, #1f1f1f); border-radius: 12px; padding: 26px; margin-bottom: 18px; color: var(--kc-text); display: flex; align-items: center; gap: 18px; border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); flex-wrap: wrap; }
  .profile-hero-body { flex: 1 1 240px; min-width: 0; }
  .profile-hero-actions { flex-shrink: 0; }
  @media (max-width: 640px) {
    .profile-hero { flex-direction: column; align-items: flex-start; padding: 22px; gap: 14px; }
    .profile-hero-body { width: 100%; }
    .profile-hero-actions { width: 100%; }
    .profile-hero-actions .btn { width: 100%; justify-content: center; }
    .profile-name { font-size: 20px !important; word-break: break-word; }
  }
  .profile-avatar-lg { width: 72px; height: 72px; border-radius: 50%; background: var(--kc-accent-grad); display: flex; align-items: center; justify-content: center; font-family: 'Montserrat', sans-serif; font-size: 26px; font-weight: 700; color: var(--kc-on-accent); border: 3px solid color-mix(in srgb, var(--kc-accent) 30%, transparent); flex-shrink: 0; }
  .profile-name { font-family: 'Cinzel', serif; font-size: 24px; font-weight: 700; text-transform: uppercase; color: var(--kc-text); letter-spacing: 0.04em; }
  .profile-meta { font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-sub); margin-top: 4px; }
  .info-item { margin-bottom: 12px; }
  .info-label { font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--kc-meta); margin-bottom: 3px; }
  .info-value { font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-text); font-weight: 500; }

  /* NOTIFICATIONS */
  .notif-panel { position: absolute; top: 48px; right: 0; width: 340px; background: var(--kc-elevated); border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.6); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); z-index: 100; overflow: hidden; }
  .notif-panel-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); }
  .notif-panel-header .title { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em; }
  .notif-panel-header .clear-all { background: none; border: none; color: var(--kc-accent); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; cursor: pointer; padding: 4px 6px; border-radius: 4px; }
  .notif-panel-header .clear-all:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .notif-panel-header .clear-all:disabled { opacity: 0.3; cursor: not-allowed; }
  .notif-panel-list { max-height: 380px; overflow-y: auto; }
  .notif-row { position: relative; overflow: hidden; }
  .notif-row .delete-bg { position: absolute; inset: 0; background: linear-gradient(90deg, transparent 0%, rgba(184,50,50,0.15) 30%, #b83232 100%); display: flex; align-items: center; justify-content: flex-end; padding-right: 18px; color: #fff; font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; pointer-events: none; opacity: 0; transition: opacity 0.15s; }
  .notif-row.is-swiping .delete-bg { opacity: 1; }
  .notif-row .notif-content-swipe { position: relative; background: var(--kc-elevated); touch-action: pan-y; user-select: none; }
  .notif-row .notif-content-swipe.snap { transition: transform 0.22s cubic-bezier(0.4,0,0.2,1); }
  .notif-item { padding: 12px 42px 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); cursor: pointer; transition: background 0.1s; }
  .notif-item:hover { background: rgba(255,255,255,0.04); }
  .notif-item:last-child { border-bottom: none; }
  .notif-title { font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 500; color: var(--kc-text); line-height: 1.4; }
  .notif-time { font-family: 'Montserrat', sans-serif; font-size: 10px; color: var(--kc-meta); margin-top: 3px; }
  .notif-birthday { background: color-mix(in srgb, var(--kc-accent) 6%, transparent); }
  .notif-row .desktop-delete-btn { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); width: 26px; height: 26px; border-radius: 50%; background: rgba(184,50,50,0.18); border: 1px solid rgba(184,50,50,0.30); color: #ff6b6b; font-size: 13px; cursor: pointer; opacity: 1; transition: opacity 0.15s, background 0.15s; display: flex; align-items: center; justify-content: center; padding: 0; z-index: 2; }
  .notif-row:hover .desktop-delete-btn { background: rgba(184,50,50,0.30); }
  .undo-toast { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: var(--kc-surface); border-top: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-text); }
  .undo-toast .undo-text { flex: 1; }
  .undo-toast .undo-btn { background: var(--kc-accent); color: var(--kc-on-accent); border: none; padding: 6px 14px; border-radius: 6px; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; cursor: pointer; }
  .undo-toast .undo-btn:hover { opacity: 0.9; }
  .undo-progress { height: 2px; background: var(--kc-accent); animation: kc-undo-progress 3s linear forwards; transform-origin: left; }
  @keyframes kc-undo-progress { from { transform: scaleX(1); } to { transform: scaleX(0); } }
  @media (max-width: 768px) {
    .notif-panel { position: fixed; top: 60px; right: 8px; left: 8px; width: auto; max-width: none; }
  }
  .notif-empty { padding: 32px 18px; text-align: center; color: var(--kc-meta); font-family: 'Montserrat', sans-serif; font-size: 13px; }

  /* EMPTY */
  .empty { text-align: center; padding: 44px 20px; color: var(--kc-meta); }
  .empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.6; }
  .empty-text { font-family: 'Montserrat', sans-serif; font-size: 13px; }

  /* LOG */
  .log-item { display: flex; gap: 12px; padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .log-item:last-child { border-bottom: none; }
  .log-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--kc-accent); margin-top: 6px; flex-shrink: 0; }
  .log-text { font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-text); }
  .log-time { font-family: 'Montserrat', sans-serif; font-size: 10px; color: var(--kc-meta); margin-top: 3px; }

  /* QR */
  .qr-box { background: var(--kc-elevated); border-radius: 12px; padding: 24px; text-align: center; color: var(--kc-text); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); }
  .qr-placeholder { width: 180px; height: 180px; background: var(--kc-surface); border-radius: 8px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; font-size: 64px; color: var(--kc-on-accent); }

  /* VISITOR WELCOME */
  .visitor-bg { min-height: 100vh; background: var(--kc-surface); display: flex; align-items: center; justify-content: center; padding: 24px; position: relative; overflow: hidden; }
  .visitor-bg::before { content:''; position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 20% 0%, color-mix(in srgb, var(--kc-accent) 8%, transparent) 0%, transparent 60%), radial-gradient(ellipse at 80% 100%, color-mix(in srgb, var(--kc-accent) 6%, transparent) 0%, transparent 50%); }
  .visitor-card { width: 100%; max-width: 560px; background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); border-radius: 20px; padding: 40px; box-shadow: 0 24px 80px rgba(0,0,0,0.5); position: relative; z-index: 1; }
  .visitor-logo { display: block; width: 180px; height: auto; margin: 0 auto 16px; border-radius: 12px; }
  .visitor-title { font-family: 'Cinzel', serif; font-size: 24px; font-weight: 700; color: var(--kc-text); text-align: center; text-transform: uppercase; letter-spacing: 0.06em; }
  .visitor-tagline { font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-accent); text-align: center; margin-bottom: 8px; font-style: italic; letter-spacing: 0.06em; text-transform: uppercase; }
  .visitor-intro { font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-sub); text-align: center; margin: 14px 0 24px; line-height: 1.6; }

  /* PRINT */
  @media print {
    body, html, #root { background: var(--kc-surface) !important; color: #000 !important; }
    .sidebar, .topbar, .btn, .notif-btn, .bottom-nav-bar { display: none !important; }
    .content { padding: 0; background: var(--kc-surface); color: #000; }
    .card, .stat-card, .profile-hero { background: var(--kc-surface) !important; color: #000 !important; border: 1px solid #ccc; box-shadow: none; }
    .card-title, .stat-value, .profile-name { color: #000 !important; }
    td, th { color: #000 !important; background: var(--kc-surface) !important; }
  }

  /* MOBILE TOPBAR LOGO */
  .topbar-mobile-logo { display: none; align-items: center; gap: 10px; }
  .topbar-mobile-logo img { width: 32px; height: 32px; border-radius: 6px; }
  .topbar-mobile-logo span { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em; }

  /* PASSWORD VISIBILITY TOGGLE */
  .password-field-wrap { position: relative; width: 100%; }
  .password-field-wrap input { padding-right: 44px; }
  .password-toggle {
    position: absolute; top: 50%; right: 10px; transform: translateY(-50%);
    width: 30px; height: 30px; border-radius: 6px;
    border: none; background: transparent; cursor: pointer;
    color: var(--kc-sub); font-size: 16px; line-height: 1;
    display: flex; align-items: center; justify-content: center; padding: 0;
    transition: color 0.15s, background 0.15s;
  }
  .password-toggle:hover { color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 8%, transparent); }
  .password-field-wrap.login-pw input { padding-right: 48px; }

  /* DASHBOARD CLICKABLE STATES */
  .dash-link { cursor: pointer; transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s; }
  .dash-link:hover { transform: translateY(-2px); border-color: color-mix(in srgb, var(--kc-accent) 40%, transparent); box-shadow: 0 8px 24px rgba(0,0,0,0.5); }
  .dash-row-link { cursor: pointer; transition: background 0.15s; }
  .dash-row-link:hover { background: color-mix(in srgb, var(--kc-accent) 6%, transparent); }

  /* RESPONSIVE */
  @media (max-width: 768px) {
    .sidebar { display: none; }
    .content { padding: 14px; padding-bottom: 108px; }
    .topbar { padding: 12px 14px; }
    .topbar-title { font-size: 15px; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .topbar-mobile-logo { display: flex; }
    /* hide desktop top-bar bell on mobile — replaced by Notifications tab in bottom nav */
    .topbar-right .notif-btn { display: none; }
    .stats-grid { grid-template-columns: repeat(2, 1fr); }
    .form-grid { grid-template-columns: 1fr; }
    .visitor-card, .login-card { padding: 28px 22px; }
  }

  /* BOTTOM NAV (mobile only) */
  /* ================================================================
     PHASE-3 MOBILE BOTTOM NAV BAR
     Three states via [data-state] on .bottom-nav-bar:
       full   — default 72 px + 18 px safe-area, icons + labels.
       mini   — icons-only 44 px, semi-transparent + backdrop-blur.
       hidden — translateY(100%), fully off-screen.
     Scroll rules live in the BottomNavBar component. Transitions
     0.3 s ease on transform + height. Desktop (> 768 px) hides the
     whole nav via the existing media query below.
     ================================================================ */
  .bottom-nav-bar {
    display: none;
    position: fixed; left: 0; right: 0; bottom: 0;
    height: 72px;
    background: var(--kc-surface);
    border-top: 1px solid var(--kc-border);
    box-shadow: 0 -4px 18px rgba(0,0,0,0.20);
    z-index: 9999;
    padding-bottom: 18px;
    transform: translateY(0);
    transition: transform 0.3s ease, height 0.3s ease, background 0.3s ease, padding-bottom 0.3s ease, backdrop-filter 0.3s ease;
    will-change: transform;
  }
  .bottom-nav-bar[data-state="hidden"] {
    transform: translateY(100%);
  }
  .bottom-nav-bar[data-state="mini"] {
    height: 44px;
    padding-bottom: 0;
    background: rgba(255,255,255,0.88);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
  }
  /* Dark-mode mini uses a translucent near-black so blur reads correctly */
  .dark .bottom-nav-bar[data-state="mini"] {
    background: rgba(10,10,10,0.90);
  }
  /* Dark-mode full-state bottom nav — spec calls for #161616 (sits
     between --kc-dark-bg #111 and --kc-dark-surface #1A1A1A). */
  .dark .bottom-nav-bar[data-state="full"] {
    background: #161616;
    border-top-color: var(--kc-dark-border);
  }
  .dark .bottom-nav-tab.active { color: var(--kc-dark-accent); }
  .dark .bottom-nav-tab.active::before { background: var(--kc-dark-accent); box-shadow: 0 0 6px var(--kc-dark-accent); }
  .bottom-nav-inner {
    display: grid; grid-template-columns: repeat(5, 1fr);
    height: 100%; width: 100%; max-width: 640px; margin: 0 auto;
  }
  .bottom-nav-tab {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 3px; border: none; background: none; cursor: pointer; padding: 6px 4px;
    color: var(--kc-sub); text-decoration: none;
    font-family: 'Inter', sans-serif;
    transition: color 0.15s;
    position: relative;
  }
  .bottom-nav-tab .bn-icon { font-size: 20px; line-height: 1; position: relative; display: inline-flex; align-items: center; justify-content: center; }
  .bottom-nav-tab.active { color: var(--kc-accent); }
  .bottom-nav-tab.active .bn-icon img.kc-custom-icon { filter: drop-shadow(0 0 6px rgba(91,110,245,0.55)); }
  .dark .bottom-nav-tab.active .bn-icon img.kc-custom-icon { filter: drop-shadow(0 0 8px color-mix(in srgb, var(--kc-accent) 70%, transparent)); }
  /* 2.5 px accent indicator line ABOVE the icon on the active tab */
  .bottom-nav-tab.active::before {
    content: '';
    position: absolute;
    top: 6px;
    left: 30%;
    right: 30%;
    height: 2.5px;
    background: var(--kc-accent);
    border-radius: 2px;
    box-shadow: 0 0 6px var(--kc-accent);
  }
  .bottom-nav-tab .bn-label {
    font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
    transition: opacity 0.2s ease, height 0.2s ease;
  }
  /* Hide labels in mini state */
  .bottom-nav-bar[data-state="mini"] .bottom-nav-tab .bn-label {
    opacity: 0;
    height: 0;
    overflow: hidden;
    margin: 0;
  }
  .bottom-nav-tab:hover { color: var(--kc-accent); }
  .bn-badge {
    position: absolute; top: -6px; right: -10px;
    min-width: 18px; height: 18px; padding: 0 5px;
    background: var(--kc-red); color: var(--kc-text); border-radius: 9px;
    border: 2px solid var(--kc-surface);
    font-family: 'Inter', sans-serif; font-size: 10px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    line-height: 1;
  }
  @media (max-width: 768px) {
    .bottom-nav-bar { display: block; }
  }

  /* ================================================================
     MORE SHEET — Phase-3 mobile drop-up.
     Slides up from below the bottom-nav-bar. Backdrop dims the
     content behind. Mobile only (< 768 px).
     ================================================================ */
  .more-sheet-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
    z-index: 9998;
    display: none;
  }
  .more-sheet-backdrop.is-open { opacity: 1; pointer-events: auto; }

  .more-sheet {
    position: fixed;
    left: 0; right: 0;
    /* Sit flush above the full-state bottom nav (72 px + 18 px safe area) */
    bottom: 90px;
    max-height: calc(100vh - 90px - 24px);
    background: var(--kc-surface);
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    box-shadow: 0 -8px 32px rgba(0,0,0,0.28);
    z-index: 9999;
    transform: translateY(100%);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      transform 0.25s ease,
      opacity 0.20s ease,
      visibility 0s linear 0.25s;
    display: none;
    flex-direction: column;
    overflow: hidden;
  }
  .dark .more-sheet { border-top: 1px solid var(--kc-border); }
  .more-sheet.is-open {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition:
      transform 0.25s ease,
      opacity 0.20s ease,
      visibility 0s linear 0s;
  }

  .more-sheet-handle {
    display: block;
    margin: 8px auto 4px;
    width: 44px; height: 4px;
    border-radius: 2px;
    background: var(--kc-border);
    border: none;
    cursor: pointer;
    padding: 0;
  }

  .more-sheet-header {
    display: flex; align-items: center; gap: 12px;
    padding: 8px 16px 14px;
    border-bottom: 1px solid var(--kc-border);
  }
  .more-sheet-avatar {
    width: 40px; height: 40px; border-radius: 50%;
    background: var(--kc-accent-bg); color: var(--kc-accent);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 700;
    flex-shrink: 0;
  }
  .more-sheet-profile-btn {
    width: 36px; height: 36px; border-radius: 10px;
    background: var(--kc-elevated); color: var(--kc-sub);
    border: none; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .more-sheet-profile-btn:hover { background: var(--kc-border); color: var(--kc-text); }

  .more-sheet-body {
    padding: 14px 16px;
    overflow-y: auto;
    flex: 1;
  }
  .more-sheet-group { margin-bottom: 14px; }
  .more-sheet-group:last-child { margin-bottom: 0; }
  .more-sheet-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  .more-sheet-item {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 6px; padding: 12px 6px;
    background: var(--kc-elevated);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    color: var(--kc-text);
    text-align: center;
    min-height: 76px;
    transition: background 0.15s;
  }
  .more-sheet-item:hover { background: var(--kc-border); }
  .more-sheet-item:active { transform: scale(0.98); }
  .more-sheet-item-icon {
    width: 28px; height: 28px;
    display: inline-flex; align-items: center; justify-content: center;
    color: var(--kc-accent);
  }
  .more-sheet-item-label {
    font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 600;
    color: var(--kc-text);
    line-height: 1.2;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .more-sheet-footer {
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom, 0));
    border-top: 1px solid var(--kc-border);
  }
  .more-sheet-signout {
    width: 100%;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px 16px;
    background: none; border: none;
    color: var(--kc-red);
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 700;
    letter-spacing: 0.02em;
    border-radius: 10px;
    cursor: pointer;
  }
  .more-sheet-signout:hover { background: rgba(239,68,68,0.10); }

  @media (max-width: 768px) {
    .more-sheet-backdrop { display: block; }
    .more-sheet { display: flex; }
  }
  /* BROWSE GRID */
  .browse-page-header { margin-bottom: 18px; }
  .browse-page-title { font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em; }
  .browse-page-sub { font-family: 'Montserrat', sans-serif; font-size: 12px; color: var(--kc-meta); margin-top: 4px; }
  .browse-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }
  @media (min-width: 540px) { .browse-grid { grid-template-columns: repeat(3, 1fr); gap: 16px; } }
  @media (min-width: 900px) { .browse-grid { grid-template-columns: repeat(4, 1fr); gap: 18px; } }
  .browse-card {
    background: var(--kc-surface);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 14px;
    aspect-ratio: 1 / 1;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 14px; padding: 18px;
    text-decoration: none; color: inherit; cursor: pointer;
    box-shadow: var(--kc-shadow);
    transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s, background 0.15s;
    position: relative;
  }
  /* Iter 80: semantic left borders on Browse tiles. Combined selectors keep
     specificity above the base .browse-card rule so the semantic colour wins. */
  .browse-card.card-green  { border-left: 3px solid var(--kc-green);  }
  .browse-card.card-amber  { border-left: 3px solid var(--kc-amber);  }
  .browse-card.card-blue   { border-left: 3px solid var(--kc-accent); }
  .browse-card.card-red    { border-left: 3px solid var(--kc-red);    }
  .browse-card.card-grey   { border-left: 3px solid var(--kc-grey);   }
  .browse-card.card-purple { border-left: 3px solid var(--kc-purple); }
  .browse-card:hover, .browse-card:focus-visible {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent);
    box-shadow: var(--kc-shadow-hover);
    background: color-mix(in srgb, var(--kc-accent) 6%, var(--kc-surface));
    outline: none;
  }
  .browse-card:active { transform: translateY(0); }
  .browse-icon-circle {
    width: 64px; height: 64px;
    border-radius: 16px;
    background: color-mix(in srgb, var(--kc-accent) 14%, transparent);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    display: flex; align-items: center; justify-content: center;
    color: var(--kc-accent); font-size: 28px;
  }
  .browse-card-label {
    font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 600;
    color: var(--kc-text); text-align: center; line-height: 1.3;
    letter-spacing: 0.02em;
  }
  .browse-card .coming-soon-pill {
    position: absolute; top: 8px; right: 8px;
    background: color-mix(in srgb, var(--kc-accent) 15%, transparent);
    color: var(--kc-accent);
    font-family: 'Montserrat', sans-serif;
    font-size: 8px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.08em;
    padding: 3px 7px; border-radius: 10px;
    border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
  }

  /* DEPARTMENTS */
  .dept-grid {
    display: grid; gap: 16px;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }
  .dept-card {
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 14px; padding: 0; cursor: pointer;
    transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s;
    display: flex; flex-direction: column;
    overflow: hidden;
    box-shadow: var(--kc-shadow);
  }
  .dept-card:hover { transform: translateY(-2px); border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); box-shadow: var(--kc-shadow-hover); }
  /* Semantic left border — Issue 4 (Iter 79). */
  .dept-card.card-blue  { border-left: 3px solid var(--kc-accent); }
  .dept-card.card-amber { border-left: 3px solid var(--kc-amber);  }

  /* Iter 81 — Zone 1 (cover image) + Zone 2 (body) card structure. */
  .listing-card-body {
    position: relative;
    padding: var(--space-md);
    min-height: 80px;
    background: var(--kc-surface);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .listing-card-logo-badge {
    position: absolute;
    left: var(--space-md);
    top: -20px;
    width: 40px; height: 40px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    background: #ffffff;
    overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 6px rgba(0,0,0,0.25);
    z-index: 3;
  }
  .listing-card-logo-badge img { width: 100%; height: 100%; object-fit: cover; }
  /* Iter 82 — category / priority pills that overlay the cover in Zone 1. */
  .listing-card-pill {
    background: rgba(255,255,255,0.20);
    color: #ffffff;
    border-radius: var(--kc-radius-sm);
    padding: var(--space-xs) var(--space-sm);
    font-family: 'Inter', sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.1;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }
  .listing-card-desc {
    font-size: 12px; color: var(--kc-sub); line-height: 1.35;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    overflow: hidden; text-overflow: ellipsis;
  }
  .dept-card-header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .dept-logo {
    width: 52px; height: 52px; border-radius: 12px;
    background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    display: flex; align-items: center; justify-content: center;
    color: var(--kc-accent); font-size: 24px; flex-shrink: 0; overflow: hidden;
  }
  .dept-logo img { width: 100%; height: 100%; object-fit: cover; }
  .dept-name {
    flex: 1 1 140px; min-width: 0;
    font-family: 'Cinzel', serif; font-size: 15px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em;
    /* Allow wrapping to a second line instead of truncating when badges
       (Donations / priority / override) crowd the header row. */
    overflow-wrap: break-word; word-break: break-word; line-height: 1.2;
  }
  .priority-badge {
    font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.08em;
    padding: 4px 8px; border-radius: 8px; border: 1px solid;
  }
  .priority-badge.high { background: color-mix(in srgb, var(--kc-accent) 16%, transparent); color: var(--kc-accent); border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent); }
  .priority-badge.medium { background: rgba(192,192,192,0.10); color: #c0c0c0; border-color: rgba(192,192,192,0.30); }
  .priority-badge.low { background: rgba(255,255,255,0.04); color: var(--kc-sub); border-color: var(--kc-meta); }
  .dept-leader-line { display: flex; align-items: center; gap: 8px; font-family: 'Montserrat',sans-serif; font-size: 12px; color: var(--kc-sub); }
  .dept-stats { display: flex; gap: 12px; font-family: 'Montserrat',sans-serif; font-size: 11px; color: var(--kc-sub); }
  .dept-stat-num { color: var(--kc-accent); font-weight: 700; margin-right: 3px; }

  /* TASKS PAGE */
  .task-row {
    display: flex; align-items: center; gap: 12px;
    background: var(--kc-elevated); border: 1px solid var(--kc-border);
    border-radius: 10px; padding: 12px 14px; margin-bottom: 8px;
    transition: border-color 0.12s, background 0.12s;
  }
  /* Phase 6 semantic borders — Issue 4. Combined selectors keep specificity
     above the base .task-row rule so the card-{variant} colour wins. */
  .task-row.card-red   { border-left: 3px solid var(--kc-red);    }
  .task-row.card-blue  { border-left: 3px solid var(--kc-accent); }
  .task-row.card-green { border-left: 3px solid var(--kc-green);  }
  .task-row.card-amber { border-left: 3px solid var(--kc-amber);  }
  .task-row:hover { border-color: color-mix(in srgb, var(--kc-accent) 25%, transparent); }
  .task-row.is-done { opacity: 0.6; }
  .task-row.is-done .task-title { text-decoration: line-through; color: var(--kc-sub); }
  .task-check {
    width: 22px; height: 22px; border-radius: 50%;
    border: 2px solid color-mix(in srgb, var(--kc-accent) 40%, transparent);
    background: transparent; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    color: var(--kc-on-accent); font-size: 13px; font-weight: 700;
    flex-shrink: 0; transition: all 0.12s;
  }
  .task-check:hover { border-color: var(--kc-accent); }
  .task-check.checked { background: var(--kc-accent); border-color: var(--kc-accent); }
  .task-main { flex: 1; min-width: 0; cursor: pointer; }
  .task-title { font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; color: var(--kc-text); }
  .task-meta { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-sub); margin-top: 3px; display: flex; gap: 10px; flex-wrap: wrap; }
  .task-meta .tm-pill {
    background: rgba(255,255,255,0.05); padding: 2px 7px; border-radius: 9px;
    border: 1px solid rgba(255,255,255,0.08);
  }
  .task-meta .tm-pill.repeat { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); color: var(--kc-accent); }
  .task-meta .tm-pill.overdue { background: rgba(184,50,50,0.15); border-color: rgba(184,50,50,0.40); color: #ff8a8a; }

  .tasks-section-label {
    font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 700;
    color: var(--kc-meta); text-transform: uppercase; letter-spacing: 0.08em;
    margin: 16px 0 8px 4px;
  }

  /* Appointment row */
  .appt-row {
    display: flex; flex-direction: column; gap: 12px;
    background: var(--kc-elevated); border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px; padding: 16px; margin-bottom: 8px;
  }
  .appt-row-top { display: flex; flex-direction: row; align-items: flex-start; gap: 12px; }
  .appt-row-actions {
    display: flex; flex-wrap: wrap; gap: 8px;
    justify-content: flex-start; width: 100%;
    padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.06);
  }
  .appt-row-actions .btn { min-width: 0; }
  .appt-row-actions .btn.btn-danger { padding-left: 14px; padding-right: 14px; }
  .appt-row.clickable { cursor: pointer; transition: border-color 0.15s ease; }
  .appt-row.clickable:hover { border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); }
  .appt-date-box {
    width: 56px; flex-shrink: 0;
    background: rgba(96,165,250,0.12); color: #60a5fa;
    border: 1px solid rgba(96,165,250,0.30);
    border-radius: 9px; padding: 8px 6px; text-align: center;
    font-family: 'Montserrat', sans-serif;
  }
  .appt-date-box .a-month { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .appt-date-box .a-day { font-size: 17px; font-weight: 700; line-height: 1; }
  .appt-main { flex: 1; min-width: 0; }
  .appt-title { font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; color: var(--kc-text); }
  .appt-meta { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-sub); margin-top: 3px; }

  .appt-status {
    font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    padding: 4px 9px; border-radius: 10px;
    border: 1px solid;
  }
  .appt-status.confirmed   { background: rgba(74,222,128,0.14); color: #4ade80; border-color: rgba(74,222,128,0.40); }
  .appt-status.pending     { background: color-mix(in srgb, var(--kc-accent) 14%, transparent); color: var(--kc-accent); border-color: color-mix(in srgb, var(--kc-accent) 40%, transparent); }
  .appt-status.cancelled   { background: rgba(255,107,107,0.14); color: #ff8a8a; border-color: rgba(255,107,107,0.40); }
  .appt-status.rescheduled { background: rgba(96,165,250,0.14); color: #60a5fa; border-color: rgba(96,165,250,0.40); }

  /* Floating action button */
  .fab-btn {
    position: fixed; right: 22px; bottom: 92px;
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--kc-accent); color: var(--kc-on-accent);
    border: none; cursor: pointer;
    font-size: 28px; font-weight: 700;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--kc-accent) 40%, transparent), 0 4px 12px rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center;
    z-index: 200; transition: transform 0.15s, filter 0.15s, box-shadow 0.15s;
  }
  .fab-btn:hover { filter: brightness(1.08); transform: translateY(-2px); }
  .fab-btn:active { transform: translateY(0); }
  @media (min-width: 769px) {
    .fab-btn { bottom: 28px; right: 28px; }
  }

  /* CHECK-INS PAGE */
  .checkins-topbar {
    display: flex; align-items: stretch; gap: 12px;
    margin-bottom: 18px; flex-wrap: wrap;
  }
  .checkins-search {
    flex: 1 1 260px; min-width: 0; position: relative;
  }
  .checkins-search input {
    width: 100%; height: 44px;
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 10px; color: var(--kc-text);
    padding: 0 14px 0 40px;
    font-family: 'Montserrat', sans-serif; font-size: 13px;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .checkins-search input:focus {
    outline: none; border-color: var(--kc-accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--kc-accent) 18%, transparent);
  }
  .checkins-search .search-icon {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
    color: var(--kc-meta); font-size: 14px; pointer-events: none;
  }
  .checkins-action-btn {
    height: 44px; padding: 0 18px;
    background: var(--kc-accent); color: var(--kc-on-accent);
    border: 1px solid var(--kc-accent); border-radius: 10px;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    cursor: pointer; display: inline-flex; align-items: center; gap: 10px;
    transition: filter 0.15s, transform 0.15s;
    position: relative;
  }
  .checkins-action-btn:hover { filter: brightness(1.08); }
  .checkins-action-btn:active { transform: scale(0.98); }
  .checkins-action-btn .ca-icon {
    color: var(--kc-text); font-size: 16px; line-height: 1;
    display: inline-flex; align-items: center;
  }
  .checkins-action-btn .ca-arrow {
    color: var(--kc-text); font-size: 10px; line-height: 1;
    display: inline-flex; align-items: center;
    transition: transform 0.15s;
  }
  .checkins-action-btn.is-open .ca-arrow { transform: rotate(180deg); }

  .checkins-action-wrap { position: relative; }
  .checkins-action-menu {
    position: absolute; top: calc(100% + 6px); right: 0;
    min-width: 220px; background: var(--kc-elevated);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 32%, transparent);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.6);
    z-index: 60; padding: 6px;
  }
  .checkins-action-menu button {
    width: 100%; text-align: left;
    background: transparent; border: none;
    padding: 11px 14px; border-radius: 7px;
    color: var(--kc-text); cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 500;
    display: flex; align-items: center; gap: 10px;
  }
  .checkins-action-menu button:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); color: var(--kc-accent); }
  .checkins-action-menu .menu-icon { font-size: 15px; }

  .checkins-tabs {
    display: flex; gap: 4px;
    border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    margin-bottom: 20px;
  }
  .checkins-tab {
    background: transparent; border: none;
    padding: 12px 22px;
    color: var(--kc-sub);
    font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color 0.15s, border-color 0.15s;
  }
  .checkins-tab:hover { color: var(--kc-text); }
  .checkins-tab.active { color: var(--kc-accent); border-bottom-color: var(--kc-accent); }

  /* Large calendar preview tile (click to open full calendar) */
  .calendar-preview {
    background: var(--kc-surface);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent);
    border-radius: 14px;
    padding: 22px;
    cursor: pointer;
    transition: border-color 0.18s, box-shadow 0.18s, transform 0.15s;
    box-shadow: 0 4px 18px rgba(0,0,0,0.5);
    width: 100%; max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    min-width: 0;
  }
  .calendar-preview:hover {
    border-color: var(--kc-accent);
    box-shadow: 0 10px 30px rgba(0,0,0,0.55), 0 0 0 1px color-mix(in srgb, var(--kc-accent) 15%, transparent);
  }
  .calendar-preview:active { transform: translateY(1px); }
  .calendar-preview-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 18px; gap: 8px; flex-wrap: wrap;
  }
  .calendar-preview-month {
    font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em;
  }
  .calendar-preview-cta {
    font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-accent);
    font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
    display: inline-flex; align-items: center; gap: 6px;
  }

  /* Full month calendar grid (used by preview + full calendar page) */
  .cal-grid {
    display: grid; grid-template-columns: repeat(7, 1fr);
    gap: 6px;
    width: 100%; max-width: 100%;
    overflow: hidden;            /* never spill horizontally */
    box-sizing: border-box;
    min-width: 0;                /* allow shrinking inside grid/flex parents */
  }
  .cal-grid .cal-weekday {
    font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 700;
    color: var(--kc-meta); text-align: center; padding: 6px 0;
    text-transform: uppercase; letter-spacing: 0.08em;
    min-width: 0;                /* shrink with column */
  }
  .cal-day {
    aspect-ratio: 1/1;
    width: 100%; min-width: 0;   /* fully fluid inside its 1fr column */
    box-sizing: border-box;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    position: relative;
    color: var(--kc-text);
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 500;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
    padding: 0;                   /* no fixed padding — content sits flush */
  }
  .cal-day:hover { background: color-mix(in srgb, var(--kc-accent) 8%, transparent); border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); }
  .cal-day.empty { background: transparent; border-color: transparent; cursor: default; }
  .cal-day.is-other-month { color: var(--kc-meta); }
  .cal-day.is-today {
    color: var(--kc-on-accent); background: var(--kc-accent); border-color: var(--kc-accent); font-weight: 700;
  }
  .cal-day.is-today:hover { filter: brightness(1.04); }
  .cal-day.is-selected {
    outline: 2px solid var(--kc-accent); outline-offset: 1px;
  }
  .cal-day .cal-dot {
    position: absolute; bottom: 6px;
    width: 4px; height: 4px; border-radius: 50%;
    background: var(--kc-accent);
  }
  .cal-day.is-today .cal-dot { background: var(--kc-surface); }
  .cal-day .cal-dots {
    position: absolute; bottom: 5px; display: flex; gap: 2px; align-items: center;
  }
  .cal-day .cal-dots span {
    width: 4px; height: 4px; border-radius: 50%; background: var(--kc-accent);
  }
  .cal-day .cal-dots .cal-dot.kind-event       { background: #e8c84a; }
  .cal-day .cal-dots .cal-dot.kind-service     { background: var(--kc-accent); }
  .cal-day .cal-dots .cal-dot.kind-appointment { background: #60a5fa; }
  .cal-day .cal-dots .cal-dot.kind-blockout    { background: #ff6b6b; }
  .cal-day.is-today .cal-dots .cal-dot { background: var(--kc-surface); }
  .cal-day .cal-dot-overflow {
    font-family: 'Montserrat', sans-serif; font-size: 8px; font-weight: 700;
    color: var(--kc-sub); margin-left: 2px;
  }
  .cal-day.is-today .cal-dot-overflow { color: rgba(26,26,26,0.75); }

  /* ===== Advanced Calendar v2 (Point 7) ===== */
  /* Week strip (mobile) */
  .cal-mobile-layout { display: flex; flex-direction: column; gap: 14px; }
  .cal-week-strip {
    display: grid; grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 6px; padding: 4px 0;
  }
  .cal-week-day {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px; padding: 8px 4px;
    color: var(--kc-text);
    font-family: 'Montserrat', sans-serif; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    position: relative; min-height: 64px;
    transition: background 0.12s, border-color 0.12s;
  }
  .cal-week-day:hover { background: color-mix(in srgb, var(--kc-accent) 8%, transparent); border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); }
  .cal-week-day .cwd-name { font-size: 9px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-sub); }
  .cal-week-day .cwd-num  { font-size: 17px; font-weight: 700; color: var(--kc-text); line-height: 1; }
  .cal-week-day.is-selected { background: color-mix(in srgb, var(--kc-accent) 15%, transparent); border-color: var(--kc-accent); }
  .cal-week-day.is-today {
    background: var(--kc-accent); border-color: var(--kc-accent);
  }
  .cal-week-day.is-today .cwd-name, .cal-week-day.is-today .cwd-num { color: var(--kc-on-accent); }
  .cal-week-day.is-today.is-selected { outline: 2px solid #FFFFFF; outline-offset: 1px; }
  .cal-week-day .cwd-dots {
    display: flex; gap: 2px; align-items: center; margin-top: 2px;
  }
  .cal-week-day .cwd-dots .cal-dot {
    width: 5px; height: 5px; border-radius: 50%; background: var(--kc-accent); display: inline-block;
  }
  .cal-week-day .cwd-dots .cal-dot.kind-event       { background: #e8c84a; }
  .cal-week-day .cwd-dots .cal-dot.kind-service     { background: var(--kc-accent); }
  .cal-week-day .cwd-dots .cal-dot.kind-appointment { background: #60a5fa; }
  .cal-week-day .cwd-dots .cal-dot.kind-blockout    { background: #ff6b6b; }
  .cal-week-day.is-today .cwd-dots .cal-dot { background: var(--kc-surface); }
  .cal-week-day .cwd-overflow { font-size: 8px; font-weight: 700; color: var(--kc-sub); margin-left: 2px; }
  .cal-week-day.is-today .cwd-overflow { color: rgba(26,26,26,0.8); }
  .cal-week-day.has-items { box-shadow: inset 0 -2px 0 color-mix(in srgb, var(--kc-accent) 45%, transparent); }
  .cal-week-day.is-today.has-items { box-shadow: none; }

  /* Mobile agenda */
  .cal-agenda {
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 14px; padding: 16px;
  }

  /* Show-all button under the day detail list */
  .full-cal-show-all {
    background: transparent; border: 1px dashed color-mix(in srgb, var(--kc-accent) 45%, transparent);
    color: var(--kc-accent); border-radius: 9px; padding: 9px 12px;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em; cursor: pointer;
    text-align: center;
  }
  .full-cal-show-all:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border-color: var(--kc-accent); }

  .fci-kind-ico { margin-right: 4px; }

  /* Day-detail overlay modal */
  .cal-day-modal {
    width: min(560px, 100%); max-height: 80vh;
    background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    border-radius: 16px; overflow: hidden;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6);
  }
  .cal-day-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 20px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    background: linear-gradient(180deg, color-mix(in srgb, var(--kc-accent) 6%, transparent), transparent);
  }
  .cal-day-modal-title {
    font-family: 'Cinzel', serif; font-size: 16px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em;
  }
  .cal-day-modal-close {
    background: transparent; border: none; color: var(--kc-sub);
    font-size: 26px; line-height: 1; cursor: pointer; padding: 0 6px;
  }
  .cal-day-modal-close:hover { color: var(--kc-accent); }
  .cal-day-modal-body { padding: 16px 20px; overflow-y: auto; }

  /* Sticky Quick-Intake FAB */
  .cal-fab-wrap {
    position: fixed; z-index: 60;
    right: 18px; bottom: 84px;            /* sits above mobile bottom nav (≈ 72px) */
    display: flex; flex-direction: column; align-items: flex-end; gap: 10px;
  }
  @media (min-width: 900px) {
    .cal-fab-wrap { bottom: 28px; right: 28px; }
  }
  .cal-fab-btn {
    width: 56px; height: 56px; border-radius: 50%;
    background: var(--kc-accent); color: var(--kc-on-accent);
    border: none; cursor: pointer;
    font-size: 28px; font-weight: 700; line-height: 1;
    display: inline-flex; align-items: center; justify-content: center;
    box-shadow: 0 10px 30px color-mix(in srgb, var(--kc-accent) 35%, transparent), 0 4px 12px rgba(0,0,0,0.45);
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .cal-fab-btn:hover { transform: translateY(-2px); box-shadow: 0 14px 40px color-mix(in srgb, var(--kc-accent) 45%, transparent), 0 6px 16px rgba(0,0,0,0.55); }
  .cal-fab-btn.is-open { background: var(--kc-surface); color: var(--kc-accent); border: 1.5px solid var(--kc-accent); }
  .cal-fab-menu {
    background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    border-radius: 14px; padding: 8px; min-width: 240px;
    box-shadow: 0 18px 44px rgba(0,0,0,0.55);
    display: flex; flex-direction: column; gap: 4px;
  }
  .cal-fab-item {
    background: transparent; border: none; border-radius: 9px;
    padding: 10px 12px; cursor: pointer; text-align: left;
    display: flex; align-items: center; gap: 12px; color: var(--kc-text);
    font-family: 'Montserrat', sans-serif;
    transition: background 0.12s;
  }
  .cal-fab-item:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .cal-fab-item .cfi-ico {
    width: 32px; height: 32px; border-radius: 8px;
    background: color-mix(in srgb, var(--kc-accent) 12%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    display: inline-flex; align-items: center; justify-content: center; font-size: 15px;
    flex-shrink: 0;
  }
  .cal-fab-item .cfi-text { display: flex; flex-direction: column; }
  .cal-fab-item .cfi-title { font-size: 13px; font-weight: 600; color: var(--kc-text); }
  .cal-fab-item .cfi-sub   { font-size: 11px; color: var(--kc-sub); margin-top: 2px; }

  /* ===== Calendar v3 — View tabs, Scope tabs, Year/Week/Day, Add Chooser ===== */
  .cal-view-tabs, .cal-scope-tabs {
    display: flex; gap: 4px; padding: 4px;
    background: color-mix(in srgb, var(--kc-accent) 6%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 10px; margin-bottom: 10px; flex-wrap: wrap;
  }
  .cal-view-tab, .cal-scope-tab {
    flex: 1 1 0; min-width: 60px; padding: 8px 10px;
    background: transparent; border: 0; border-radius: 7px; cursor: pointer;
    color: var(--kc-sub); font-family: 'Montserrat', sans-serif;
    font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
    transition: background 0.15s, color 0.15s;
  }
  .cal-view-tab:hover, .cal-scope-tab:hover { color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 8%, transparent); }
  .cal-view-tab.is-active, .cal-scope-tab.is-active {
    background: var(--kc-accent); color: var(--kc-on-accent);
  }

  /* Year view — 12 mini months */
  .cal-year-grid {
    display: grid; gap: 18px;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  .cal-year-month {
    background: color-mix(in srgb, var(--kc-accent) 4%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 12px; padding: 10px 10px 14px;
    text-align: left; cursor: pointer; transition: transform 0.12s, border-color 0.15s, background 0.15s;
    color: var(--kc-text); display: flex; flex-direction: column;
  }
  .cal-year-month:hover { transform: translateY(-2px); border-color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 8%, transparent); }
  .cymo-title {
    font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700;
    color: var(--kc-accent); text-transform: uppercase; letter-spacing: 0.06em;
    margin-bottom: 6px; text-align: center;
  }
  .cymo-grid {
    display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px;
  }
  .cymo-h { font-size: 9px; color: var(--kc-meta); text-align: center; padding: 2px 0; }
  .cymo-cell {
    position: relative; min-height: 22px;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; color: var(--kc-sub); border-radius: 4px;
    padding: 2px 0;
  }
  .cymo-cell.empty { visibility: hidden; }
  .cymo-cell.has-items { color: var(--kc-text); background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .cymo-num { line-height: 1; }
  .cymo-dot { position: absolute; bottom: 2px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; }
  .cymo-dot.kind-event       { background: #e8c84a; }
  .cymo-dot.kind-service     { background: var(--kc-accent); }
  .cymo-dot.kind-appointment { background: #5fb1ff; }
  .cymo-dot.kind-blockout    { background: #ff8a65; }
  .cymo-dot.kind-birthday    { background: #ff8fb6; }

  /* Week view */
  .cal-week-view {
    background: color-mix(in srgb, var(--kc-accent) 3%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 12px; overflow-x: auto; overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }
  .cal-week-view > * { min-width: 56px; }
  .cal-week-head, .cal-week-row, .cal-week-allday {
    display: grid; grid-template-columns: 56px repeat(7, minmax(80px, 1fr));
    min-width: 616px;  /* 56 + 7*80 ensures no clipping on narrow screens */
  }
  .cal-week-head { background: color-mix(in srgb, var(--kc-accent) 8%, transparent); border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); }
  .cal-week-corner { padding: 8px; }
  .cal-week-col-head {
    padding: 8px 4px; text-align: center; background: transparent;
    border: 0; border-left: 1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent);
    color: var(--kc-sub); cursor: pointer;
  }
  .cal-week-col-head.is-selected { background: color-mix(in srgb, var(--kc-accent) 18%, transparent); }
  .cal-week-col-head.is-today .cwch-num { color: var(--kc-accent); font-weight: 800; }
  .cwch-day { font-size: 9px; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-meta); }
  .cwch-num { font-family: 'Cinzel', serif; font-size: 16px; color: var(--kc-text); margin-top: 2px; }
  .cal-week-allday {
    border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    min-height: 36px; background: color-mix(in srgb, var(--kc-accent) 3%, transparent);
  }
  .cal-week-allday-label { font-size: 9px; color: var(--kc-meta); padding: 8px 4px; text-align: right; letter-spacing: 0.04em; text-transform: uppercase; }
  .cal-week-allday-cell { padding: 4px; border-left: 1px solid color-mix(in srgb, var(--kc-accent) 8%, transparent); display: flex; flex-direction: column; gap: 2px; }
  .cal-week-body { max-height: 60vh; overflow-y: auto; overflow-x: hidden; }
  .cal-week-row { border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 6%, transparent); min-height: 38px; }
  .cal-week-time-col { font-size: 10px; color: var(--kc-meta); padding: 2px 6px; text-align: right; }
  .cal-week-cell { border-left: 1px solid color-mix(in srgb, var(--kc-accent) 6%, transparent); padding: 2px; display: flex; flex-direction: column; gap: 2px; }
  .cal-week-add-empty {
    background: transparent; border: 1px dashed color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 6px;
    color: color-mix(in srgb, var(--kc-accent) 65%, transparent); padding: 2px 4px; cursor: pointer; font-size: 12px;
  }
  .cal-week-add-empty:hover { background: color-mix(in srgb, var(--kc-accent) 8%, transparent); border-color: var(--kc-accent); color: var(--kc-accent); }

  /* Day view */
  .cal-day-view {
    background: color-mix(in srgb, var(--kc-accent) 3%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 12px; padding: 8px; max-height: 70vh; overflow-y: auto;
  }
  .cal-day-header { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; }
  .cal-day-title { font-family: 'Cinzel', serif; font-size: 14px; color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em; }
  .cal-day-allday { display: grid; grid-template-columns: 56px 1fr; gap: 6px; padding: 6px 0; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent); margin-bottom: 6px; }
  .cal-day-allday-label { font-size: 9px; color: var(--kc-meta); text-align: right; padding-top: 4px; letter-spacing: 0.04em; text-transform: uppercase; }
  .cal-day-allday-items { display: flex; flex-direction: column; gap: 4px; }
  .cal-day-row { display: grid; grid-template-columns: 56px 1fr; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 6%, transparent); min-height: 40px; align-items: flex-start; padding: 2px 0; }
  .cal-day-time { font-size: 10px; color: var(--kc-meta); padding: 4px 6px; text-align: right; }
  .cal-day-slot { padding: 2px 4px; display: flex; flex-direction: column; gap: 3px; }
  .cal-day-add-slot {
    background: transparent; border: 1px dashed color-mix(in srgb, var(--kc-accent) 20%, transparent); border-radius: 6px;
    color: color-mix(in srgb, var(--kc-accent) 50%, transparent); padding: 2px 8px; cursor: pointer; font-size: 11px;
    align-self: flex-start;
  }
  .cal-day-add-slot:hover { background: color-mix(in srgb, var(--kc-accent) 8%, transparent); border-color: var(--kc-accent); color: var(--kc-accent); }

  /* Calendar blocks (shared by week + day) */
  .cal-block {
    display: flex; align-items: center; gap: 6px;
    background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    border-radius: 6px; padding: 4px 8px;
    color: var(--kc-text); font-family: 'Montserrat', sans-serif; font-size: 11px;
    cursor: pointer; text-align: left; overflow: hidden;
    transition: background 0.12s, border-color 0.12s;
  }
  .cal-block:hover { background: color-mix(in srgb, var(--kc-accent) 22%, transparent); border-color: var(--kc-accent); }
  .cal-block.kind-event    { background: rgba(232,200,74,0.18); border-color: rgba(232,200,74,0.40); }
  .cal-block.kind-service  { background: color-mix(in srgb, var(--kc-accent) 18%, transparent); border-color: color-mix(in srgb, var(--kc-accent) 40%, transparent); }
  .cal-block.kind-appointment { background: rgba(95,177,255,0.18); border-color: rgba(95,177,255,0.40); }
  .cal-block.kind-blockout { background: rgba(255,138,101,0.18); border-color: rgba(255,138,101,0.40); }
  .cal-block.kind-birthday { background: rgba(255,143,182,0.18); border-color: rgba(255,143,182,0.45); }
  .cal-block-ico { font-size: 12px; flex-shrink: 0; }
  .cal-block-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }

  /* Anti-overlay: every cal modal scrolls within a capped height */
  .cal-modal-scrollable {
    max-height: 80vh; overflow-y: auto;
    /* Center via modal-overlay's flex; ensure no fixed positioning forced */
  }

  /* Add Chooser rows */
  .cal-chooser-row {
    display: flex; align-items: center; gap: 12px; padding: 12px 14px;
    background: color-mix(in srgb, var(--kc-accent) 6%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 10px; color: var(--kc-text); cursor: pointer; text-align: left;
    width: 100%;
  }
  .cal-chooser-row:hover { background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border-color: var(--kc-accent); }
  .cal-chooser-row .cfi-ico { font-size: 26px; }
  .cal-chooser-row .cfi-text { display: flex; flex-direction: column; }
  .cal-chooser-row .cfi-title { font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; color: var(--kc-accent); letter-spacing: 0.04em; text-transform: uppercase; }
  .cal-chooser-row .cfi-sub { font-size: 11px; color: var(--kc-sub); margin-top: 2px; }

  /* Day-of-week chips for weekly recurrence */
  .cal-dow-chip {
    width: 38px; height: 38px; border-radius: 50%;
    background: transparent; border: 1.5px solid color-mix(in srgb, var(--kc-accent) 35%, transparent);
    color: var(--kc-sub); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600;
    cursor: pointer; transition: all 0.15s;
  }
  .cal-dow-chip:hover { border-color: var(--kc-accent); color: var(--kc-accent); }
  .cal-dow-chip.is-selected { background: var(--kc-accent); color: var(--kc-on-accent); border-color: var(--kc-accent); }
  .cal-dow-chip:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Standalone Add FAB (admins only, calendar v3) */
  .cal-add-fab {
    position: fixed; right: 18px; bottom: 84px; z-index: 60;
  }
  @media (min-width: 900px) {
    .cal-add-fab { right: 28px; bottom: 28px; }
  }

  /* Toast */
  .cal-toast {
    position: fixed; left: 50%; transform: translateX(-50%);
    bottom: 92px; z-index: 80;
    background: var(--kc-surface); color: var(--kc-accent); border: 1px solid var(--kc-accent);
    border-radius: 999px; padding: 10px 18px;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600;
    box-shadow: 0 14px 36px rgba(0,0,0,0.55);
  }

  /* Anti-overlap on extreme small screens (320px) */
  @media (max-width: 360px) {
    .cal-view-tab, .cal-scope-tab { font-size: 11px; padding: 6px 4px; min-width: 44px; }
    .cal-week-head, .cal-week-row, .cal-week-allday { grid-template-columns: 40px repeat(7, minmax(80px, 1fr)); min-width: 600px; }
    .cal-week-time-col { font-size: 9px; padding: 2px 3px; }
    .cwch-num { font-size: 14px; }
  }

  /* Full calendar page */
  .full-cal-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px; margin-bottom: 16px; flex-wrap: wrap;
  }
  .full-cal-month {
    font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em;
  }
  .full-cal-nav { display: flex; align-items: center; gap: 6px; }
  .full-cal-nav-btn {
    width: 36px; height: 36px; border-radius: 9px;
    background: rgba(255,255,255,0.04); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    color: var(--kc-text); cursor: pointer; font-size: 14px;
    display: inline-flex; align-items: center; justify-content: center;
  }
  .full-cal-nav-btn:hover { background: color-mix(in srgb, var(--kc-accent) 12%, transparent); border-color: var(--kc-accent); color: var(--kc-accent); }
  .full-cal-today-btn {
    background: transparent;
    color: var(--kc-accent); border: 1.5px solid color-mix(in srgb, var(--kc-accent) 50%, transparent);
    height: 36px; padding: 0 14px; border-radius: 9px;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em; cursor: pointer;
  }
  .full-cal-today-btn:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border-color: var(--kc-accent); }

  .full-cal-layout {
    display: grid; grid-template-columns: 1fr 320px; gap: 18px;
  }
  @media (max-width: 900px) { .full-cal-layout { grid-template-columns: 1fr; } }

  .full-cal-grid-wrap {
    background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 14px; padding: 18px;
    width: 100%; max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    min-width: 0;
  }
  .full-cal-grid-wrap .cal-day { font-size: 14px; }

  /* === Mobile sizing — keep 7 columns fully visible at all narrow widths === */
  @media (max-width: 480px) {
    .calendar-preview { padding: 14px; }
    .full-cal-grid-wrap { padding: 10px; }
    .cal-grid { gap: 4px; }
    .cal-grid .cal-weekday { font-size: 9px; padding: 4px 0; }
    .cal-day { font-size: 12px; border-radius: 6px; padding: 0; }
    .full-cal-grid-wrap .cal-day { font-size: 12px; }
    .cal-day .cal-dots { bottom: 3px; gap: 1px; }
    .cal-day .cal-dots span,
    .cal-day .cal-dots .cal-dot { width: 4px; height: 4px; }
    .cal-day .cal-dot-overflow { font-size: 7px; }
  }
  @media (max-width: 389px) {
    .calendar-preview { padding: 10px; }
    .full-cal-grid-wrap { padding: 8px; }
    .cal-grid { gap: 3px; }
    .cal-day { font-size: 11px; }              /* user-requested minimum */
    .full-cal-grid-wrap .cal-day { font-size: 11px; }
    .cal-grid .cal-weekday { font-size: 8px; padding: 3px 0; letter-spacing: 0.04em; }
  }

  .full-cal-detail {
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 14px; padding: 18px; align-self: start;
    position: sticky; top: 12px;
  }
  .full-cal-detail-date {
    font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.06em;
    margin-bottom: 14px; padding-bottom: 10px;
    border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
  }
  .full-cal-detail-list { display: flex; flex-direction: column; gap: 10px; }
  .full-cal-item {
    background: var(--kc-surface); border: 1px solid var(--kc-border);
    border-left: 3px solid var(--kc-accent); border-radius: 8px;
    padding: 10px 12px; cursor: pointer;
    transition: border-color 0.12s, background 0.12s;
  }
  .full-cal-item:hover { background: var(--kc-surface); border-color: color-mix(in srgb, var(--kc-accent) 35%, transparent); }
  /* Phase 5E: semantic left-border comes from the .card-{blue|green|amber|red}
     class added at render time via kindVariant(item). Combined selectors keep
     specificity above the base .full-cal-item rule so the semantic colour wins. */
  .full-cal-item.card-blue  { border-left-color: var(--kc-accent); }
  .full-cal-item.card-green { border-left-color: var(--kc-green);  }
  .full-cal-item.card-amber { border-left-color: var(--kc-amber);  }
  .full-cal-item.card-red   { border-left-color: var(--kc-red);    }
  .full-cal-item .fci-kind {
    font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 700;
    color: var(--kc-meta); text-transform: uppercase; letter-spacing: 0.10em;
  }
  .full-cal-item .fci-title {
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600;
    color: var(--kc-text); margin-top: 2px;
  }
  .full-cal-item .fci-meta {
    font-family: 'Montserrat', sans-serif; font-size: 11px;
    color: var(--kc-meta); margin-top: 3px;
  }
  .full-cal-detail-empty {
    font-family: 'Montserrat', sans-serif; font-size: 12px;
    color: var(--kc-meta);
    padding: 18px 4px; text-align: center;
  }

  /* Blockouts list */
  .blockout-row {
    display: flex; align-items: center; gap: 14px;
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
    border-radius: 10px; padding: 12px 14px; margin-bottom: 10px;
  }
  .blockout-row .bo-date-box {
    width: 56px; flex-shrink: 0; background: rgba(255,107,107,0.12);
    color: #ff8a8a; border-radius: 9px; padding: 8px 6px; text-align: center;
    border: 1px solid rgba(255,107,107,0.25);
    font-family: 'Montserrat', sans-serif;
  }
  .blockout-row .bo-date-box .bd-month { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
  .blockout-row .bo-date-box .bd-day { font-size: 17px; font-weight: 700; line-height: 1; }
  .blockout-row .bo-main { flex: 1; min-width: 0; }
  .blockout-row .bo-name { font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600; color: var(--kc-text); }
  .blockout-row .bo-meta { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-sub); margin-top: 3px; }
  .blockout-row .bo-applies {
    font-family: 'Montserrat', sans-serif; font-size: 10px; color: var(--kc-accent);
    text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700;
  }

  /* Blockout form modal-specific */
  .bo-form-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  }
  @media (max-width: 540px) { .bo-form-row { grid-template-columns: 1fr; } }
  .bo-form-label {
    display: block;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700;
    color: var(--kc-sub);
    text-transform: uppercase; letter-spacing: 0.06em;
    margin-bottom: 6px;
  }
  .bo-applies-options { display: flex; gap: 8px; margin-bottom: 8px; }
  .bo-applies-pill {
    flex: 1; padding: 9px 12px; border-radius: 9px;
    background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.10);
    color: var(--kc-sub); cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    text-align: center; transition: all 0.15s;
  }
  .bo-applies-pill:hover { border-color: color-mix(in srgb, var(--kc-accent) 40%, transparent); color: var(--kc-text); }
  .bo-applies-pill.active {
    background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border-color: var(--kc-accent); color: var(--kc-accent);
  }
  .bo-user-picker {
    max-height: 220px; overflow-y: auto;
    background: var(--kc-surface);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 9px;
    padding: 6px;
  }
  .bo-user-row {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 6px;
    cursor: pointer; font-family: 'Montserrat', sans-serif;
    font-size: 12px; color: var(--kc-text);
    transition: background 0.12s;
  }
  .bo-user-row:hover { background: rgba(255,255,255,0.05); }
  .bo-user-row.selected { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); }
  .bo-user-check {
    width: 18px; height: 18px; border-radius: 4px;
    border: 1.5px solid rgba(255,255,255,0.30);
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 11px; color: var(--kc-on-accent); flex-shrink: 0;
  }
  .bo-user-row.selected .bo-user-check { background: var(--kc-accent); border-color: var(--kc-accent); }
  .bo-allday {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 0;
    font-family: 'Montserrat', sans-serif; font-size: 13px; color: var(--kc-text);
    cursor: pointer;
  }
  .bo-allday input { width: 18px; height: 18px; accent-color: var(--kc-accent); cursor: pointer; }

  /* DASHBOARD WIDGETS */
  .dashboard-header {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 12px; margin-bottom: 18px;
  }
  .dashboard-title-block .dashboard-greeting {
    font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em;
  }
  .dashboard-title-block .dashboard-sub {
    font-family: 'Montserrat', sans-serif; font-size: 12px;
    color: var(--kc-meta); margin-top: 4px;
  }
  .customize-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; color: var(--kc-accent);
    border: 1.5px solid color-mix(in srgb, var(--kc-accent) 50%, transparent);
    border-radius: 8px; padding: 9px 16px;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    cursor: pointer; transition: all 0.15s;
  }
  .customize-btn:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border-color: var(--kc-accent); }

  .widgets-stack { display: flex; flex-direction: column; gap: 16px; }

  .widget-card {
    position: relative;
    background: var(--kc-elevated);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.4);
    overflow: hidden;
    transition: opacity 0.15s, transform 0.15s, border-color 0.15s, box-shadow 0.15s;
  }
  .widget-card.is-dragging { opacity: 0.45; transform: scale(0.98); }
  .widget-card.is-drop-target {
    border-color: var(--kc-accent);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--kc-accent) 30%, transparent), 0 4px 18px rgba(0,0,0,0.5);
  }

  .widget-header {
    display: flex; align-items: center; gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
    background: linear-gradient(180deg, color-mix(in srgb, var(--kc-accent) 4%, transparent), transparent);
  }
  .widget-drag-handle {
    width: 28px; height: 28px; border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.04);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    cursor: grab; color: var(--kc-meta); font-size: 14px;
    touch-action: none;
  }
  .widget-drag-handle:active { cursor: grabbing; background: color-mix(in srgb, var(--kc-accent) 10%, transparent); color: var(--kc-accent); }

  .widget-icon-box {
    width: 36px; height: 36px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
  }
  .widget-title {
    flex: 1; min-width: 0;
    font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .widget-controls { display: flex; align-items: center; gap: 4px; }
  .widget-ctrl-btn {
    width: 28px; height: 28px; border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    cursor: pointer; color: var(--kc-sub);
    font-size: 13px; padding: 0; transition: all 0.15s;
  }
  .widget-ctrl-btn:hover:not(:disabled) { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border-color: color-mix(in srgb, var(--kc-accent) 30%, transparent); color: var(--kc-accent); }
  .widget-ctrl-btn:disabled { opacity: 0.25; cursor: not-allowed; }
  .widget-ctrl-btn.remove { color: var(--kc-sub); }
  .widget-ctrl-btn.remove:hover { background: rgba(184,50,50,0.15); border-color: rgba(184,50,50,0.40); color: #ff6b6b; }

  .widget-body { padding: 4px 0; }
  .widget-body-padded { padding: 16px; }
  .widget-empty {
    padding: 24px 18px; text-align: center;
    color: var(--kc-meta);
    font-family: 'Montserrat', sans-serif; font-size: 12px;
  }
  .widget-empty .we-icon { font-size: 28px; margin-bottom: 8px; opacity: 0.7; }

  .widget-row {
    display: flex; align-items: center; gap: 12px;
    padding: 11px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    cursor: pointer; transition: background 0.12s;
  }
  .widget-row:last-child { border-bottom: none; }
  .widget-row:hover { background: color-mix(in srgb, var(--kc-accent) 6%, transparent); }
  .widget-row-main { flex: 1; min-width: 0; }
  .widget-row-title { font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 500; color: var(--kc-text); }
  .widget-row-meta { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-meta); margin-top: 2px; }
  .widget-row-trailing { font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-meta); text-align: right; flex-shrink: 0; }

  /* ===== My Week at a Glance widget — horizontal 7-day strip ===== */
  .my-week-strip-wrap { position: relative; min-width: 0; }
  .my-week-scroll {
    display: flex; gap: 10px; padding: 12px 14px 14px;
    overflow-x: auto; overflow-y: hidden; scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    min-width: 0;
  }
  .my-week-scroll::-webkit-scrollbar { height: 6px; }
  .my-week-scroll::-webkit-scrollbar-thumb { background: color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 99px; }
  .my-week-day {
    flex: 0 0 132px; min-width: 132px; max-width: 132px;
    background: var(--kc-elevated); border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 12px; padding: 10px;
    color: var(--kc-text); cursor: pointer; text-align: left;
    transition: transform 0.15s, border-color 0.15s, background 0.15s;
    scroll-snap-align: start;
    display: flex; flex-direction: column;
    overflow: hidden;
  }
  .my-week-day:hover { transform: translateY(-2px); border-color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 8%, transparent); }
  .my-week-day.is-today {
    border-color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 10%, transparent);
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--kc-accent) 20%, transparent) inset;
  }
  .mwd-header {
    display: flex; align-items: baseline; justify-content: space-between;
    padding-bottom: 8px; margin-bottom: 8px;
    border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent);
    min-width: 0;
  }
  .mwd-dow {
    font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700;
    letter-spacing: 0.10em; color: var(--kc-accent);
  }
  .mwd-num {
    font-family: 'Cinzel', serif; font-size: 22px; font-weight: 700; color: var(--kc-text);
    line-height: 1;
  }
  .mwd-body { display: flex; flex-direction: column; gap: 5px; min-height: 60px; min-width: 0; }
  .mwd-empty {
    font-family: 'Montserrat', sans-serif; font-size: 11px;
    color: var(--kc-meta); font-style: italic;
    text-align: center; padding: 12px 4px;
  }
  .mwd-item {
    display: flex; align-items: center; gap: 6px;
    background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent);
    border-radius: 6px; padding: 4px 6px;
    font-family: 'Montserrat', sans-serif; font-size: 11px; color: var(--kc-text);
    cursor: pointer; transition: background 0.12s;
    min-width: 0; overflow: hidden;
  }
  .mwd-item:hover { background: color-mix(in srgb, var(--kc-accent) 18%, transparent); }
  .mwd-item.kind-event       { background: rgba(232,200,74,0.14); border-color: rgba(232,200,74,0.35); }
  .mwd-item.kind-service     { background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border-color: color-mix(in srgb, var(--kc-accent) 35%, transparent); }
  .mwd-item.kind-appointment { background: rgba(95,177,255,0.14); border-color: rgba(95,177,255,0.35); }
  .mwd-item.kind-blockout    { background: rgba(255,138,101,0.14); border-color: rgba(255,138,101,0.35); }
  .mwd-item.kind-birthday    { background: rgba(255,143,182,0.14); border-color: rgba(255,143,182,0.40); }
  .mwd-item-ico { font-size: 12px; flex-shrink: 0; }
  .mwd-item-title { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .mwd-item-add {
    width: 20px; height: 20px; border-radius: 50%;
    background: color-mix(in srgb, var(--kc-accent) 18%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 40%, transparent);
    color: var(--kc-accent); cursor: pointer; flex-shrink: 0; font-size: 13px;
    line-height: 1; padding: 0; display: inline-flex; align-items: center; justify-content: center;
    transition: background 0.12s;
  }
  .mwd-item-add:hover { background: var(--kc-accent); color: var(--kc-on-accent); }
  .mwd-overflow {
    font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 600;
    color: var(--kc-accent); letter-spacing: 0.04em; text-transform: uppercase;
    text-align: center; padding: 2px;
  }
  .my-week-toast {
    position: absolute; left: 50%; bottom: 8px; transform: translateX(-50%);
    background: var(--kc-surface); color: var(--kc-accent); border: 1px solid var(--kc-accent);
    border-radius: 999px; padding: 6px 14px;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600;
    box-shadow: 0 12px 28px rgba(0,0,0,0.5); z-index: 5;
  }
  @media (max-width: 480px) {
    .my-week-day { flex-basis: 116px; min-width: 116px; max-width: 116px; }
  }

  .widget-empty-add {
    background: var(--kc-elevated); border: 1.5px dashed color-mix(in srgb, var(--kc-accent) 40%, transparent);
    border-radius: 14px; padding: 36px 24px; text-align: center;
  }
  .widget-empty-add .wea-icon { font-size: 38px; margin-bottom: 10px; opacity: 0.75; color: var(--kc-accent); }
  .widget-empty-add .wea-title {
    font-family: 'Cinzel', serif; font-size: 16px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;
  }
  .widget-empty-add .wea-sub {
    font-family: 'Montserrat', sans-serif; font-size: 12px;
    color: var(--kc-meta); margin-bottom: 18px;
  }

  /* CUSTOMIZE DASHBOARD MODAL */
  .picker-list { display: flex; flex-direction: column; gap: 10px; }
  .picker-row {
    display: flex; align-items: center; gap: 12px;
    background: var(--kc-surface);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent);
    border-radius: 10px;
    padding: 12px 14px;
    transition: border-color 0.15s, background 0.15s;
  }
  .picker-row.is-added { border-color: color-mix(in srgb, var(--kc-accent) 35%, transparent); background: color-mix(in srgb, var(--kc-accent) 5%, transparent); }
  .picker-row .picker-icon {
    width: 36px; height: 36px; border-radius: 9px;
    display: flex; align-items: center; justify-content: center; font-size: 18px;
    flex-shrink: 0;
  }
  .picker-row .picker-info { flex: 1; min-width: 0; }
  .picker-row .picker-name {
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600;
    color: var(--kc-text);
  }
  .picker-row .picker-desc {
    font-family: 'Montserrat', sans-serif; font-size: 11px;
    color: var(--kc-meta); margin-top: 2px;
  }
  .picker-row .picker-action {
    flex-shrink: 0;
    background: transparent;
    border: 1.5px solid color-mix(in srgb, var(--kc-accent) 40%, transparent);
    color: var(--kc-accent);
    padding: 7px 14px; border-radius: 8px;
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.06em;
    cursor: pointer; transition: all 0.15s;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .picker-row .picker-action:hover { background: color-mix(in srgb, var(--kc-accent) 10%, transparent); border-color: var(--kc-accent); }
  .picker-row .picker-action.added {
    background: color-mix(in srgb, var(--kc-accent) 15%, transparent); color: var(--kc-accent); border-color: color-mix(in srgb, var(--kc-accent) 45%, transparent);
  }
  .picker-row .picker-action.added:hover {
    background: rgba(184,50,50,0.15); border-color: rgba(184,50,50,0.40); color: #ff6b6b;
  }
  .picker-footer-action {
    background: transparent; border: none;
    color: var(--kc-sub);
    font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.06em;
    cursor: pointer; padding: 6px 8px; border-radius: 6px;
  }
  .picker-footer-action:hover { color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 6%, transparent); }

  @media (max-width: 540px) {
    .widget-header { padding: 12px 12px; gap: 8px; }
    .widget-title { font-size: 12px; }
    .widget-controls .widget-ctrl-btn { width: 26px; height: 26px; }
  }

  /* COMING SOON PAGE */
  .coming-soon-wrap {
    display: flex; align-items: center; justify-content: center;
    min-height: calc(100vh - 200px);
    padding: 32px 16px;
  }
  .coming-soon-card {
    background: var(--kc-elevated);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent);
    border-radius: 16px;
    padding: 48px 32px;
    max-width: 520px; width: 100%;
    text-align: center;
  }
  .coming-soon-icon-box {
    width: 84px; height: 84px;
    margin: 0 auto 18px;
    border-radius: 20px;
    background: color-mix(in srgb, var(--kc-accent) 14%, transparent);
    border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    display: flex; align-items: center; justify-content: center;
    font-size: 38px; color: var(--kc-accent);
  }
  .coming-soon-pill-lg {
    display: inline-block;
    background: color-mix(in srgb, var(--kc-accent) 15%, transparent);
    color: var(--kc-accent);
    font-family: 'Montserrat', sans-serif;
    font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.10em;
    padding: 5px 12px; border-radius: 12px;
    border: 1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent);
    margin-bottom: 14px;
  }
  .coming-soon-title {
    font-family: 'Cinzel', serif; font-size: 26px; font-weight: 700;
    color: var(--kc-text); text-transform: uppercase; letter-spacing: 0.05em;
    margin-bottom: 10px;
  }
  .coming-soon-desc {
    font-family: 'Montserrat', sans-serif; font-size: 13px;
    color: var(--kc-sub); line-height: 1.6; margin-bottom: 26px;
  }

  .mb-4{margin-bottom:4px}.mb-8{margin-bottom:8px}.mb-12{margin-bottom:12px}.mb-16{margin-bottom:16px}.mb-20{margin-bottom:20px}.mb-24{margin-bottom:24px}
  .flex{display:flex}.flex-wrap{flex-wrap:wrap}.items-center{align-items:center}.justify-between{justify-content:space-between}
  .gap-6{gap:6px}.gap-8{gap:8px}.gap-10{gap:10px}.gap-12{gap:12px}.gap-16{gap:16px}
  .text-sm{font-size:13px}.text-xs{font-size:11px}.text-muted{color:var(--kc-meta)}.font-600{font-weight:600}.w-full{width:100%}
`;

// ============================================================
// SEED DATA
// ============================================================
// NOTE: SEED_USERS is used ONLY as fallback initial state for the
// frontend store. Authentication is performed server-side against
// bcrypt-hashed passwords seeded into MongoDB at backend startup
// (see /app/backend/server.py SEED_USERS). The frontend never
// validates passwords against this list, so no `password` field
// is included here.
const SEED_USERS = [
  { id:"u1", username:"pastor", role:"superadmin", isSeed:true, memberId:"m1", name:"Pastor Emmanuel", email:"pastor@kogc.org", sessionTimeout: 30 },
  { id:"u2", username:"elder.sarah", role:"limited_admin", isSeed:true, memberId:"m2", name:"Sarah Mensah", email:"sarah@kogc.org", sessionTimeout: 30 },
  { id:"u3", username:"deacon.james", role:"team_member", isSeed:true, memberId:"m3", name:"James Osei", email:"james@kogc.org", assignedMembers:["m4","m5","m6","m7"], sessionTimeout: 30 },
  { id:"u4", username:"sister.ruth", role:"team_member", isSeed:true, memberId:"m8", name:"Ruth Adjei", email:"ruth@kogc.org", assignedMembers:["m9","m10","m11"], sessionTimeout: 30 },
  { id:"u5", username:"pastor.kwame", role:"pastor", isSeed:true, memberId:"m7", name:"Pastor Kwame Asare", email:"kwame@kogc.org", assignedMembers:["m4","m5"], sessionTimeout: 30 },
];
const SEED_MEMBERS = [
  { id:"m1", firstName:"Emmanuel", lastName:"Asante", dob:"1968-03-15", gender:"Male", maritalStatus:"Married", phone:[{label:"Mobile",number:"+1 555-0101"}], email:[{label:"Personal",address:"pastor@church.org"}], address:"123 Faith Ave", language:"English", baptized:true, baptizedBy:"Rev. Kwame", baptizedWhere:"Grace Chapel", baptizedDate:"1990-04-12", spiritualBackground:"Raised in church", departments:["Leadership","Preaching"], joinedDate:"1990-01-01", memberStatus:"Active", additionType:"Joined by decision", additionDate:"1990-01-01", tags:["Married","Leadership"], familyId:"f1", photo:null },
  { id:"m2", firstName:"Sarah", lastName:"Mensah", dob:"1975-07-22", gender:"Female", maritalStatus:"Married", phone:[{label:"Mobile",number:"+1 555-0102"}], email:[{label:"Personal",address:"sarah@church.org"}], address:"45 Blessing St", language:"English", baptized:true, baptizedBy:"Pastor Emmanuel", baptizedWhere:"Our Church", baptizedDate:"2001-06-10", spiritualBackground:"Different denomination", departments:["Women Ministry","Administration"], joinedDate:"2001-01-01", memberStatus:"Active", additionType:"Transfer from another church", additionDate:"2001-01-01", tags:["Married","Elder"], familyId:"f2", photo:null },
  { id:"m3", firstName:"James", lastName:"Osei", dob:"1982-11-30", gender:"Male", maritalStatus:"Single", phone:[{label:"Mobile",number:"+1 555-0103"}], email:[{label:"Personal",address:"james@email.com"}], address:"78 Hope Road", language:"English", baptized:true, baptizedBy:"Pastor Emmanuel", baptizedWhere:"Our Church", baptizedDate:"2010-05-20", spiritualBackground:"Joined from different religion", departments:["Ushers","Outreach"], joinedDate:"2010-01-01", memberStatus:"Active", additionType:"Joined by decision", additionDate:"2010-01-01", tags:["Single","Deacon"], familyId:null, photo:null },
  { id:"m4", firstName:"Abena", lastName:"Frimpong", dob:"1990-04-05", gender:"Female", maritalStatus:"Single", phone:[{label:"Mobile",number:"+1 555-0104"}], email:[{label:"Personal",address:"abena@email.com"}], address:"12 Grace Lane", language:"Twi", baptized:false, spiritualBackground:"New to faith", departments:["Choir"], joinedDate:"2022-03-10", memberStatus:"New Member", additionType:"Joined by decision", additionDate:"2022-03-10", tags:["Single","Youth"], invitedBy:"m3", familyId:null, photo:null },
  { id:"m5", firstName:"Kofi", lastName:"Boateng", dob:"1988-09-14", gender:"Male", maritalStatus:"Married", phone:[{label:"Mobile",number:"+1 555-0105"}], email:[{label:"Personal",address:"kofi@email.com"}], address:"55 Mercy Ave", language:"English", baptized:true, baptizedBy:"Pastor Emmanuel", baptizedWhere:"Our Church", baptizedDate:"2015-08-30", spiritualBackground:"Raised in church", departments:["Choir","Youth"], joinedDate:"2015-01-01", memberStatus:"Active", additionType:"Joined by decision", additionDate:"2015-01-01", tags:["Married","Adult"], familyId:"f3", photo:null },
  { id:"m6", firstName:"Akua", lastName:"Mensah", dob:"1995-12-20", gender:"Female", maritalStatus:"Single", phone:[{label:"Mobile",number:"+1 555-0106"}], email:[{label:"Personal",address:"akua@email.com"}], address:"33 Salvation Rd", language:"French", baptized:false, spiritualBackground:"Different denomination", departments:[], joinedDate:"2023-06-01", memberStatus:"Guest", additionType:"Joined by decision", additionDate:"2023-06-01", tags:["Single","Young Adult"], familyId:null, photo:null },
  { id:"m7", firstName:"Kwame", lastName:"Asare", dob:"1970-02-18", gender:"Male", maritalStatus:"Married", phone:[{label:"Mobile",number:"+1 555-0107"},{label:"Home",number:"+1 555-0207"}], email:[{label:"Personal",address:"kwame@email.com"}], address:"88 Covenant Close", language:"English", baptized:true, baptizedBy:"Rev. Poku", baptizedWhere:"Bethel Church", baptizedDate:"1995-03-25", spiritualBackground:"Transfer from another church", departments:["Elders","Finance"], joinedDate:"2005-07-15", memberStatus:"Active", additionType:"Transfer from another church", additionDate:"2005-07-15", tags:["Married","Elder","Senior"], familyId:"f4", photo:null },
  { id:"m8", firstName:"Ruth", lastName:"Adjei", dob:"1985-06-10", gender:"Female", maritalStatus:"Single", phone:[{label:"Mobile",number:"+1 555-0108"}], email:[{label:"Personal",address:"ruth@email.com"}], address:"22 Providence Walk", language:"English", baptized:true, baptizedBy:"Pastor Emmanuel", baptizedWhere:"Our Church", baptizedDate:"2008-04-15", spiritualBackground:"Raised in church", departments:["Children Ministry","Administration"], joinedDate:"2008-01-01", memberStatus:"Active", additionType:"Joined by decision", additionDate:"2008-01-01", tags:["Single","Team Leader"], familyId:null, photo:null },
  { id:"m9", firstName:"Ama", lastName:"Darko", dob:"1998-03-25", gender:"Female", maritalStatus:"Single", phone:[{label:"Mobile",number:"+1 555-0109"}], email:[{label:"Personal",address:"ama@email.com"}], address:"14 Joy Street", language:"Twi", baptized:false, spiritualBackground:"New to faith", departments:["Youth"], joinedDate:"2024-01-15", memberStatus:"New Member", additionType:"Joined by decision", additionDate:"2024-01-15", tags:["Single","Youth"], invitedBy:"m8", familyId:null, photo:null },
  { id:"m10", firstName:"Benjamin", lastName:"Agyei", dob:"1960-08-05", gender:"Male", maritalStatus:"Widowed", phone:[{label:"Mobile",number:"+1 555-0110"}], email:[], address:"67 Faithfulness Ave", language:"English", baptized:true, baptizedBy:"Rev. Asante", baptizedWhere:"Redemption Church", baptizedDate:"1985-12-01", spiritualBackground:"Raised in church", departments:["Elders"], joinedDate:"1995-05-20", memberStatus:"Active", additionType:"Transfer from another church", additionDate:"1995-05-20", tags:["Widowed","Elder","Senior"], familyId:"f5", photo:null },
  { id:"m11", firstName:"Grace", lastName:"Nkrumah", dob:"2002-11-12", gender:"Female", maritalStatus:"Single", phone:[{label:"Mobile",number:"+1 555-0111"}], email:[{label:"Personal",address:"grace@email.com"}], address:"9 Blessing Close", language:"English", baptized:false, spiritualBackground:"Raised in church", departments:["Youth","Choir"], joinedDate:"2020-09-01", memberStatus:"Active", additionType:"Joined by decision", additionDate:"2020-09-01", tags:["Single","Youth"], familyId:null, photo:null },
];
const SEED_FAMILIES = [
  { id:"f1", name:"Asante Family", members:["m1"], head:"m1" },
  { id:"f2", name:"Mensah Family", members:["m2"], head:"m2" },
  { id:"f3", name:"Boateng Family", members:["m5"], head:"m5" },
  { id:"f4", name:"Asare Family", members:["m7"], head:"m7" },
  { id:"f5", name:"Agyei Family", members:["m10"], head:"m10" },
];
const SEED_DEPARTMENTS = ["Leadership","Preaching","Women Ministry","Administration","Ushers","Outreach","Choir","Youth","Children Ministry","Finance","Elders","Worship","Media","Prayer"];
const SEED_SERVICES = [
  { id:"s1", name:"Sunday Morning Service", type:"recurring", dayOfWeek:0 },
  { id:"s2", name:"Wednesday Bible Study", type:"recurring", dayOfWeek:3 },
  { id:"s3", name:"Friday Prayer Night", type:"recurring", dayOfWeek:5 },
];
const SEED_EVENTS = [
  { id:"ev1", name:"Easter Conference 2026", date:"2026-04-20", description:"Annual Easter Conference", capacity:200, registrations:["m1","m2","m3","m4"] },
  { id:"ev2", name:"Youth Retreat", date:"2026-07-15", description:"Annual youth retreat", capacity:50, registrations:["m9","m11"] },
];
const SEED_FOLLOWUPS = [
  { id:"fu1", memberId:"m4", teamMemberId:"u3", date:"2026-06-01", type:"In-person", notes:"Visited Abena at home. She is doing well and excited about church.", nextVisitDate:"2026-06-15" },
  { id:"fu2", memberId:"m5", teamMemberId:"u3", date:"2026-06-03", type:"Phone call", notes:"Quick check in. Kofi mentioned he will miss Sunday due to travel.", nextVisitDate:"2026-06-17" },
  { id:"fu3", memberId:"m9", teamMemberId:"u4", date:"2026-06-05", type:"In-person", notes:"Met with Ama at the church office. She has questions about baptism.", nextVisitDate:"2026-06-12" },
];
const SEED_ATTENDANCE = [
  { id:"a1", serviceId:"s1", date:"2026-06-01", records:{ m1:"present",m2:"present",m3:"present",m4:"present",m5:"absent",m6:"absent",m7:"present",m8:"present",m9:"absent",m10:"present",m11:"present" }, absenceReasons:{ m5:"Travel",m6:"No reason given",m9:"Sick" } },
  { id:"a2", serviceId:"s2", date:"2026-06-04", records:{ m1:"present",m2:"absent",m3:"present",m4:"absent",m5:"present",m7:"present",m8:"absent",m10:"present",m11:"present" }, absenceReasons:{ m2:"No reason given",m4:"Sick",m8:"Travel" } },
];
const SEED_AUDIT = [
  { id:"au1", userId:"u1", userName:"Pastor Emmanuel", action:"Created member", detail:"Added Ama Darko", timestamp:"2026-06-01T09:00:00" },
  { id:"au2", userId:"u3", userName:"James Osei", action:"Logged follow-up", detail:"Follow-up visit with Abena Frimpong", timestamp:"2026-06-01T14:30:00" },
];
const SEED_NOTIFICATIONS = [
  { id:"n1", text:"James Osei logged a follow-up visit with Abena Frimpong", time:"Today 2:30 PM", read:false, type:"followup", ref:{followupId:"f3",memberId:"m4"} },
  { id:"n2", text:"Birthday tomorrow: Abena Frimpong (Apr 5)", time:"Today 8:00 AM", read:false, type:"birthday", ref:{memberId:"m4"} },
  { id:"n3", text:"Sunday attendance recorded — 8 present, 3 absent", time:"Jun 1, 12:00 PM", read:true, type:"attendance", ref:{} },
];

// ============================================================
// STORAGE — backed by FastAPI + MongoDB
// ============================================================
// AUTH TOKEN STORAGE — currently localStorage. This is the standard
// React+JWT pattern but is XSS-vulnerable in principle. Defence-in-depth:
//   1) Server-side JWT has an 8-hour TTL (see backend ACCESS_TTL_MIN).
//   2) Client enforces a 30-minute idle timeout that wipes the token.
//   3) No third-party scripts are loaded, reducing XSS attack surface.
// For higher-stakes deployments, migrate to httpOnly+SameSite=Strict
// cookies with a CSRF token, which requires backend cookie endpoints.
// API_BASE, kcFetch, getToken/setToken/authHeaders, and the _apiGet/Post/Put/Delete
// helpers are imported from ../lib/apiClient. Keeping JWT in localStorage with
// short idle timeout enforced client-side; backend issues 8-hour HS256 tokens.

// Keys whose empty-array response must be treated as suspicious. The frontend
// will surface a banner instead of silently writing/using []. The backend
// already prevents the wipe via Protection 3, but defense-in-depth.
const CRITICAL_LIST_KEYS = ["kc_members", "kc_users", "kc_families", "kc_attendance"];
let _criticalEmptyShown = false;

async function loadData(key, fallback) {
  try {
    const res = await kcFetch(`${API_BASE}/storage/${key}`, { headers: { ...authHeaders() } });
    if (res.status === 404) {
      // CRITICAL: Never write seed data back to the backend.
      // A 404 means the storage doc doesn't exist yet — that's fine, we just
      // return the local fallback for rendering. The doc will be created on
      // the first real user-driven save (per-record endpoint or explicit action).
      // Writing seed data here was the original cause of the production data
      // wipe — we now refuse to seed under ANY circumstances.
      return fallback;
    }
    if (!res.ok) return fallback;
    const data = await res.json();
    const value = data.value ?? fallback;
    // PROTECTION 5 — if a critical list comes back empty when the local
    // fallback has data, warn the user. Do NOT auto-overwrite.
    if (
      CRITICAL_LIST_KEYS.includes(key) &&
      Array.isArray(value) &&
      value.length === 0 &&
      Array.isArray(fallback) &&
      fallback.length > 0 &&
      !_criticalEmptyShown
    ) {
      _criticalEmptyShown = true;
      console.warn(
        `[Kingdom Connect] Critical storage key '${key}' returned [] from the backend. ` +
        "This may indicate a fresh database. Frontend is NOT auto-seeding to prevent data loss. " +
        "If this is a new install, ask the Super Admin to add records via the UI."
      );
      try {
        // Lightweight in-page banner — no router dependency.
        if (typeof window !== "undefined" && typeof document !== "undefined") {
          const id = "kc-critical-empty-banner";
          if (!document.getElementById(id)) {
            const el = document.createElement("div");
            el.id = id;
            el.style.cssText =
              "position:fixed;top:0;left:0;right:0;z-index:99999;background:#7a1d1d;color:#fff;padding:10px 16px;font-family:Montserrat,sans-serif;font-size:13px;text-align:center;box-shadow:0 2px 6px rgba(0,0,0,0.4);";
            el.innerHTML =
              `<strong>Data integrity warning</strong> — '${key}' returned an empty list. ` +
              "If you expected data here, contact the Super Admin BEFORE adding new records. " +
              "<a href='#' onclick='this.parentElement.remove();return false;' style='color:#ffd166;margin-left:12px;text-decoration:underline;'>Dismiss</a>";
            document.body.appendChild(el);
          }
        }
      } catch { /* no-op */ }
      // Return empty array (the truth from backend) but DO NOT save fallback
      return value;
    }
    return value;
  } catch { return fallback; }
}
async function saveData(key, value) {
  try {
    await kcFetch(`${API_BASE}/storage/${key}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ value }),
    });
  } catch {}
}
async function apiLogin(username, password) {
  const res = await kcFetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(()=>({detail:"Login failed"}));
    throw new Error(typeof err.detail === "string" ? err.detail : "Invalid username or password");
  }
  return res.json();
}
async function apiMe() {
  const res = await kcFetch(`${API_BASE}/auth/me`, { headers: { ...authHeaders() } });
  if (!res.ok) return null;
  return res.json();
}
// Admin user API helpers (apiAdminCreateUser/Update/Delete) — extracted to ./admin/api.js.

// ----- Follow-ups (server-enforced privacy) -----
// All follow-ups / tasks / appointments API helpers have moved to their
// respective feature folders (`./followups/api`, `./tasks/api`,
// `./appointments/api`). KingdomConnect.jsx no longer redeclares them.

// Departments — extracted to ./departments/api.js and imported at the top.
// `apiListDepartments`, `apiCreateDepartment`, `apiUpdateDepartment`, `apiDeleteDepartment`
// are re-imported from there (DepartmentsPage in this file still uses them directly).

// ============================================================
// UTILS
// ============================================================
// `fullName` and `initials` are imported from ../lib/format (see top).

// Role label + badge class shared across the app
function roleLabel(role) {
  if (role === "superadmin") return "Super Admin";
  if (role === "limited_admin") return "Limited Admin";
  if (role === "pastor") return "Pastor";
  return "Team Member";
}
function roleBadgeClass(role) {
  if (role === "superadmin") return "badge-gold";
  if (role === "limited_admin") return "badge-blue";
  if (role === "pastor") return "badge-green";
  return "badge-charcoal";
}

// `Avatar` is imported from ./shared/Avatar (see top). PhotoUploader stays here.

// PhotoUploader: file → base64 data URL → onChange(url|null). Preview + remove.

// ============================================================
// MAIN APP
// ============================================================

// MyProfilePage extracted to its feature folder.

// ============================================================
// ROUTING HELPERS
// ============================================================
function RequireRole({ role, allow, children }) {
  if (!allow.includes(role)) {
    return <Navigate to="/dashboard?denied=1" replace />;
  }
  return children;
}

function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <div className="empty" data-testid="not-found-page" style={{padding:"60px 20px"}}>
      <div className="empty-icon"><CustomIcon name="person-settings" size={40} /></div>
      <div style={{fontFamily:"'Cinzel',serif",fontSize:22,color:"var(--kc-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Page Not Found</div>
      <div className="empty-text" style={{marginBottom:18}}>The page <strong style={{color:"var(--kc-accent)"}}>{location.pathname}</strong> does not exist or has been moved.</div>
      <button className="btn btn-gold" onClick={()=>navigate("/dashboard")}>← Back to Dashboard</button>
    </div>
  );
}

function AccessDeniedBanner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const denied = searchParams.get("denied") === "1";
  useEffect(() => {
    if (denied) {
      const timer = setTimeout(() => {
        const next = new URLSearchParams(searchParams);
        next.delete("denied");
        setSearchParams(next, { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [denied, searchParams, setSearchParams]);
  if (!denied) return null;
  return (
    <div data-testid="access-denied-banner" style={{
      background: "rgba(184,50,50,0.20)", color: "#ff6b6b",
      borderBottom: "1px solid rgba(184,50,50,0.40)",
      padding: "10px 26px", fontFamily: "'Montserrat',sans-serif",
      fontSize: 13, fontWeight: 600, letterSpacing: "0.04em",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      Access Denied — you don't have permission for that page. You've been returned to the Dashboard.
    </div>
  );
}


// ============================================================
// BOTTOM NAV BAR (mobile only — replaces hamburger drawer)
// ============================================================
function BottomNavBar({ taskBadgeCount, currentUser = {}, navItems = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const isActive = (p) => path === p || path.startsWith(p + "/");
  const badgeLabel = taskBadgeCount >= 10 ? "9+" : String(taskBadgeCount);
  const [sheetOpen, setSheetOpen] = useState(false);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  // Auto-close the sheet on route change so users don't get a stale sheet
  // hanging over the new page after tapping an action.
  useEffect(() => { setSheetOpen(false); }, [path]);
  // Lock body scroll while the sheet is open (mobile-only behaviour).
  useEffect(() => {
    if (!sheetOpen) return () => {};
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);

  // ---- Phase-3 scroll-driven visibility ----------------------------------
  // Three states:
  //   full   — default (72 px + 18 px safe area, icons + labels).
  //   mini   — icons-only, 44 px, semi-transparent + backdrop-blur. Shown
  //            while scrolling UP from a scroll position > 80 px.
  //   hidden — translated fully off-screen. Shown while scrolling DOWN
  //            past 80 px from top.
  // Rules:
  //   scrollTop < 80 px            → always full
  //   scrollTop ≥ 80 px, going ↓   → hidden
  //   scrollTop ≥ 80 px, going ↑   → mini
  //   returning below 80 px        → full (regardless of direction)
  const [navState, setNavState] = useState("full");
  useEffect(() => {
    // On mobile the whole document is the scroll container (the flexbox
    // layout collapses; `.content` grows to its natural height and `window`
    // is what scrolls). On desktop `.content` scrolls, but the bottom nav
    // is hidden anyway. Listen to both and use whichever is actually moving.
    const contentEl = document.querySelector(".content");
    const getY = () => Math.max(
      window.scrollY || document.documentElement.scrollTop || 0,
      contentEl ? contentEl.scrollTop : 0,
    );
    let lastY = getY();
    let ticking = false;
    const THRESHOLD = 80;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = getY();
        const dy = y - lastY;
        if (y < THRESHOLD) {
          setNavState("full");
        } else if (dy > 2) {
          setNavState("hidden");
        } else if (dy < -2) {
          setNavState("mini");
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    if (contentEl) contentEl.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (contentEl) contentEl.removeEventListener("scroll", onScroll);
    };
  }, [path]);
  // ------------------------------------------------------------------------

  return (
    <nav
      className="bottom-nav-bar"
      data-testid="bottom-nav-bar"
      data-state={navState}
      role="navigation"
      aria-label="Primary mobile navigation"
    >
      <div className="bottom-nav-inner">
        <button
          type="button"
          className={`bottom-nav-tab ${isActive("/dashboard") ? "active" : ""}`}
          data-testid="bottom-nav-home"
          aria-label="Home"
          aria-current={isActive("/dashboard") ? "page" : undefined}
          onClick={() => navigate("/dashboard")}
        >
          <span className="bn-icon"><CustomIcon name="dashboard" size={22} /></span>
          <span className="bn-label">Home</span>
        </button>
        <button
          type="button"
          className={`bottom-nav-tab ${isActive("/tasks") ? "active" : ""}`}
          data-testid="bottom-nav-tasks"
          aria-label={taskBadgeCount > 0 ? `Tasks, ${taskBadgeCount} pending` : "Tasks"}
          aria-current={isActive("/tasks") ? "page" : undefined}
          onClick={() => navigate("/tasks")}
        >
          <span className="bn-icon">
            <CustomIcon name="clipboard" size={22} />
            {taskBadgeCount > 0 && (
              <span className="bn-badge" data-testid="bottom-nav-tasks-badge">{badgeLabel}</span>
            )}
          </span>
          <span className="bn-label">Tasks</span>
        </button>
        <button
          type="button"
          className={`bottom-nav-tab ${isActive("/notifications") ? "active" : ""}`}
          data-testid="bottom-nav-notifications"
          aria-label="Notifications"
          aria-current={isActive("/notifications") ? "page" : undefined}
          onClick={() => navigate("/notifications")}
        >
          <span className="bn-icon"><CustomIcon name="bell" size={22} /></span>
          <span className="bn-label">Alerts</span>
        </button>
        <button
          type="button"
          className={`bottom-nav-tab ${isActive("/browse") ? "active" : ""}`}
          data-testid="bottom-nav-browse"
          aria-label="Browse"
          aria-current={isActive("/browse") ? "page" : undefined}
          onClick={() => navigate("/browse")}
        >
          <span className="bn-icon"><CustomIcon name="browse" size={22} /></span>
          <span className="bn-label">Browse</span>
        </button>
        <button
          type="button"
          className={`bottom-nav-tab ${sheetOpen ? "active" : ""}`}
          data-testid="bottom-nav-more"
          aria-label="More"
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
          onClick={() => setSheetOpen((v) => !v)}
        >
          <span className="bn-icon"><CustomIcon name="hamburger" size={22} /></span>
          <span className="bn-label">More</span>
        </button>
      </div>
      <MoreSheet
        open={sheetOpen}
        onClose={closeSheet}
        currentUser={currentUser}
        navItems={navItems}
        onNavigate={(p) => { closeSheet(); navigate(p); }}
      />
    </nav>
  );
}

// ============================================================
// MORE SHEET — Phase-3 mobile drop-up bottom sheet.
// ============================================================
// Opens from the "More" tab on the bottom nav. Uses the shared
// design tokens (surface + border + accent). Role-gated actions
// are derived from the app's NAV_ITEMS registry so this stays in
// sync automatically as new pages are added.
// ============================================================
function MoreSheet({ open, onClose, currentUser, navItems, onNavigate }) {
  // The 4 primary tabs on the bottom nav — anything else is a "More" candidate.
  const PRIMARY_IDS = new Set(["dashboard", "tasks", "notifications", "browse"]);
  const role = currentUser?.role;
  const roleAllowed = (item) => Array.isArray(item.roles) ? item.roles.includes(role) : true;
  const candidates = (navItems || []).filter((n) =>
    !PRIMARY_IDS.has(n.id) && roleAllowed(n) && n.id !== "onboarding"
  );
  // Dedupe by path so items pointing to the same route (e.g. calendar +
  // check-ins → /check-ins) don't render twice.
  const seen = new Set();
  const deduped = candidates.filter((n) => {
    if (seen.has(n.path)) return false;
    seen.add(n.path);
    return true;
  });
  // Group by section preserving order from NAV_ITEMS.
  const grouped = [];
  deduped.forEach((n) => {
    let g = grouped.find((x) => x.section === n.section);
    if (!g) { g = { section: n.section, items: [] }; grouped.push(g); }
    g.items.push(n);
  });
  const signOut = () => {
    onClose();
    localStorage.removeItem("kc_token");
    window.location.href = "/login";
  };
  const nameInitials = (currentUser?.name || "").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <>
      <div
        className={`more-sheet-backdrop ${open ? "is-open" : ""}`}
        data-testid="more-sheet-backdrop"
        onClick={onClose}
        aria-hidden={!open}
      />
      <div
        className={`more-sheet ${open ? "is-open" : ""}`}
        data-testid="more-sheet"
        role="dialog"
        aria-label="More menu"
        aria-hidden={!open}
      >
        <button
          type="button"
          className="more-sheet-handle"
          data-testid="more-sheet-close"
          onClick={onClose}
          aria-label="Close"
        />
        <div className="more-sheet-header">
          <div className="more-sheet-avatar" aria-hidden="true">{nameInitials || "?"}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="text-card-primary" style={{ color: "var(--kc-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {currentUser?.name || "Signed in"}
            </div>
            <div className="text-meta">{roleLabel(role) || ""}</div>
          </div>
          <button
            type="button"
            className="more-sheet-profile-btn"
            data-testid="more-sheet-profile-btn"
            onClick={() => onNavigate("/me")}
            aria-label="Open profile"
          >
            <CustomIcon name="person" size={16} />
          </button>
        </div>
        <div className="more-sheet-body">
          {grouped.map((g) => (
            <div key={g.section} className="more-sheet-group">
              <div className="text-eyebrow" style={{ marginBottom: 6 }}>{g.section}</div>
              <div className="more-sheet-grid">
                {g.items.map((it) => (
                  <button
                    type="button"
                    key={it.id}
                    className="more-sheet-item"
                    data-testid={`more-sheet-item-${it.id}`}
                    onClick={() => onNavigate(it.path)}
                  >
                    <span className="more-sheet-item-icon">
                      {it.customIcon ? <CustomIcon name={it.customIcon} size={18} /> : it.icon}
                    </span>
                    <span className="more-sheet-item-label">{it.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="more-sheet-footer">
          <button
            type="button"
            className="more-sheet-signout"
            data-testid="more-sheet-signout"
            onClick={signOut}
          >
            <CustomIcon name="logout" size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </div>
    </>
  );
}


// ============================================================
// COMING SOON PAGE — used for Appointments, Departments, Giving, Tasks
// ============================================================
function ComingSoonPage({ title, icon, description }) {
  const navigate = useNavigate();
  return (
    <div className="coming-soon-wrap" data-testid={`coming-soon-${(title||"").toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="coming-soon-card">
        <div className="coming-soon-icon-box">{icon}</div>
        <div className="coming-soon-pill-lg">Coming Soon</div>
        <div className="coming-soon-title">{title}</div>
        <div className="coming-soon-desc">{description}</div>
        <button className="btn btn-outline" data-testid="coming-soon-back" onClick={() => navigate("/browse")}>← Back to Browse</button>
      </div>
    </div>
  );
}


// ============================================================
// BROWSE PAGE — role-based 2-col tile grid
// ============================================================
const BROWSE_TILES = {
  members:      { label: "The Flock",      customIcon: "people",         path: "/members" },
  families:     { label: "Families",       customIcon: "family",         path: "/families" },
  followups:    { label: "Follow-Ups",     customIcon: "chat",           path: "/follow-ups" },
  calendar:     { label: "Calendar",       customIcon: "calendar",       path: "/check-ins" },
  attendance:   { label: "Attendance",     customIcon: "clipboard",      path: "/attendance" },
  events:       { label: "Events",         customIcon: "event-ticket",   path: "/events" },
  services:     { label: "Services",       customIcon: "dove",           path: "/admin/services" },
  departments:  { label: "Departments",    customIcon: "tools",          path: "/departments" },
  appointments: { label: "Appointments",   customIcon: "calendar-clock", path: "/appointments" },
  analytics:    { label: "Analytics",      customIcon: "chart",          path: "/reports/analytics" },
  users:        { label: "Users & Roles",  customIcon: "person-settings", path: "/admin/users" },
  bulk_import:  { label: "Bulk Import",    customIcon: "download-inbox", path: "/admin/bulk-import" },
  qr_poster:    { label: "QR Poster",      customIcon: "qr",             path: "/admin/qr-poster" },
  giving:       { label: "My Giving",      customIcon: "money",          path: "/giving",        comingSoon: false },
};

// Iter 80: semantic left border variant per browse tile.
// Green = growth / people / offerings; Amber = pending / journey; Blue =
// scheduled / informational / admin; Grey = system / audit surfaces.
const BROWSE_TILE_VARIANT = {
  members:      "green",
  attendance:   "green",
  giving:       "green",
  followups:    "amber",
  appointments: "amber",
  finance:      "blue",
  departments:  "blue",
  calendar:     "blue",
  families:     "blue",
  analytics:    "blue",
  services:     "blue",
  events:       "blue",
  qr_poster:    "blue",
  users:        "blue",
  bulk_import:  "blue",
  settings:     "grey",
  audit:        "grey",
};

const BROWSE_ORDER_BY_ROLE = {
  superadmin:    ["members","families","calendar","attendance","events","services","departments","appointments","analytics","users","bulk_import","qr_poster","giving"],
  limited_admin: ["members","families","calendar","attendance","events","services","departments","appointments","analytics","qr_poster","giving"],
  pastor:        ["members","families","calendar","attendance","events","services","departments","appointments","analytics","qr_poster","giving"],
  team_member:   ["members","families","calendar","attendance","events","services","departments","appointments","qr_poster","giving"],
};

// Follow-Ups special card inside the Departments grid.
// Iter 83: mirrors the standard Zone 1 / Zone 2 dept-card layout so it
// visually aligns with every other department card. Cover image + text
// styling are read from appSettings (editable in Super Admin → Settings).
function FollowUpsDeptCard({ navigate }) {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const label = appSettings.followUpsLabel || "Follow-Ups";
  const customIcon = appSettings.followUpsIcon;
  const coverImage = appSettings.followUpsCoverImage || "";
  const textColor = appSettings.followUpsTextColor || "white";
  const textEffect = appSettings.followUpsTextEffect || "shadow";
  const overlay = typeof appSettings.followUpsOverlay === "number" ? appSettings.followUpsOverlay : 55;
  const target = "/followuptree";
  if (!appSettingsLoaded) {
    // Prevent a flash of default branding on first paint.
    return null;
  }
  return (
    <div
      className="dept-card card-amber"
      data-testid="dept-card-followups-special"
      onClick={() => navigate(target)}
      role="link"
      tabIndex={0}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && navigate(target)}
      style={{ cursor: "pointer", position: "relative" }}
    >
      {/* ZONE 1 — Cover image area (no title text — moved to Zone 2 in Iter 84) */}
      <div style={{ position: "relative" }}>
        <CoverImage
          value={coverImage}
          title=""
          subtitle={""}
          iconName="chat"
          overlay={overlay}
          textColor={textColor}
          textEffect={textEffect}
          data-testid="followups-cover"
          style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none", height: 160, aspectRatio: "auto" }}
        />
        {/* Priority pill top-right — Follow-Ups is fixed "High" as a care-facing feature. */}
        <div
          className="listing-card-pills"
          style={{
            position: "absolute",
            top: "var(--space-sm)",
            right: "var(--space-sm)",
            display: "flex",
            gap: 6,
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <span data-testid="followups-card-priority-badge" className="listing-card-pill">
            High
          </span>
        </div>
      </div>

      {/* ZONE 2 — Card body */}
      <div className="listing-card-body" style={{ paddingTop: "var(--space-sm)" }}>
        {/* Logo badge — straddles Zone 1/2 boundary via `top: -20px`. */}
        <div className="listing-card-logo-badge" aria-hidden="true">
          {customIcon
            ? <img src={customIcon} alt="" />
            : <CustomIcon name="chat" size={22} />}
        </div>
        {/* Department name at top of Zone 2 — same styling as standard dept cards. */}
        <div
          className="text-card-primary"
          data-testid="followups-card-label"
          style={{
            fontFamily: "'Cinzel', serif",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--kc-text)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            lineHeight: 1.2,
            paddingLeft: 52,
            minHeight: 26,
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
        >
          {label}
        </div>
        <div className="listing-card-desc">
          Visitor & member care tracking
        </div>
        <div className="flex flex-wrap" style={{ gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
          <button className="btn-secondary btn-sm" data-testid="followups-card-open" onClick={() => navigate(target)}>Open →</button>
        </div>
      </div>
    </div>
  );
}

function BrowsePage({ currentUser }) {
  const navigate = useNavigate();
  const { settings: appSettings } = useAppSettings();
  const role = currentUser?.role || "team_member";
  const tileKeys = BROWSE_ORDER_BY_ROLE[role] || BROWSE_ORDER_BY_ROLE.team_member;

  return (
    <div data-testid="browse-page">
      <div className="browse-page-header">
        <div className="browse-page-title">Browse</div>
        <div className="browse-page-sub">All your {appSettings.appName} tools in one place.</div>
      </div>
      <div className="browse-grid" data-testid="browse-grid">
        {tileKeys.map((key) => {
          const tile = BROWSE_TILES[key];
          if (!tile) return null;
          const label = key === "followups" ? appSettings.followUpsLabel : tile.label;
          return (
            <button
              key={key}
              type="button"
              className={`browse-card card-${BROWSE_TILE_VARIANT[key] || "blue"}`}
              data-testid={`browse-tile-${key}`}
              onClick={() => navigate(tile.path)}
              aria-label={tile.comingSoon ? `${label} (coming soon)` : label}
            >
              {tile.comingSoon && <span className="coming-soon-pill">Soon</span>}
              <span className="browse-icon-circle" aria-hidden="true">
                {tile.customIcon ? <CustomIcon name={tile.customIcon} size={28} /> : tile.icon}
              </span>
              <span className="browse-card-label">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ============================================================
// CHECK-INS — landing page (search + dropdown + tabs + preview)
// ============================================================

// Helpers shared by check-ins + full-calendar — now re-exported from
// `./calendar/helpers` so both this file and the new calendar module
// stay in sync (Point 7 modularization).
const ymdKey = ymdKeyExt;
const sameYMD = sameYMDExt;
const startOfDay = startOfDayExt;
const expandServiceOccurrences = expandSvcOccExt;
const buildCalendarItems = buildCalItemsExt;
const MonthGrid = CalMonthGrid;

function CheckinsLandingPage({ currentUser, isSuperAdmin, isLimitedAdmin, events, services, appointments, blockouts, setBlockouts, users, persist, addAudit, addNotification, navigate }) {
  const isAdmin = isSuperAdmin || isLimitedAdmin;
  const [tab, setTab] = useState("calendar"); // 'calendar' | 'blockouts'
  const [search, setSearch] = useState("");
  const [showBlockoutForm, setShowBlockoutForm] = useState(false);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const { itemsByDate } = useMemo(
    () => buildCalendarItems({ year, month, currentUser, events, services, appointments, blockouts }),
    [year, month, currentUser, events, services, appointments, blockouts]
  );

  // Search across events/services/appointments/blockouts (only user-visible)
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    (events || []).forEach(e => { if ((e.name || "").toLowerCase().includes(q) || (e.location || "").toLowerCase().includes(q)) out.push({ kind: "event", title: e.name, meta: formatDate(e.date), goto: "/events" }); });
    (services || []).forEach(s => { if ((s.name || "").toLowerCase().includes(q)) out.push({ kind: "service", title: s.name, meta: `${s.day || ""}${s.time ? " · " + s.time : ""}`, goto: "/admin/services" }); });
    (appointments || []).filter(a => a.userId === currentUser.id).forEach(a => {
      if ((a.title || "").toLowerCase().includes(q) || (a.with || "").toLowerCase().includes(q)) out.push({ kind: "appointment", title: a.title || "Appointment", meta: formatDate(a.date), goto: "/appointments" });
    });
    return out.slice(0, 10);
  }, [search, events, services, appointments, currentUser.id]);

  // Blockouts list (upcoming)
  const upcomingBlockouts = useMemo(() => {
    const today = startOfDay(new Date());
    return [...(blockouts || [])]
      .filter(b => b && b.startDate && new Date(b.endDate || b.startDate) >= today)
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  }, [blockouts]);

  return (
    <div data-testid="checkins-landing-page">
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
        Calendar
      </div>

      <div className="checkins-topbar">
        <div className="checkins-search">
          <span className="search-icon"><CustomIcon name="search" size={14} /></span>
          <input
            data-testid="checkins-search"
            type="search"
            placeholder="Search events, services..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {searchResults.length > 0 && (
            <div className="checkins-action-menu" data-testid="checkins-search-results" style={{ left: 0, right: "auto", width: "100%", maxHeight: 320, overflowY: "auto" }}>
              {searchResults.map((r, i) => (
                <button key={i} type="button" onClick={() => { setSearch(""); navigate(r.goto); }}>
                  <span className="menu-icon" aria-hidden="true"><CustomIcon name={r.kind === "event" ? "event-ticket" : r.kind === "service" ? "dove" : "calendar-clock"} size={14} /></span>
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 600 }}>{r.title}</span>
                    <span style={{ display: "block", fontSize: 11, color: "var(--kc-meta)" }}>{r.meta}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="checkins-tabs" role="tablist">
        <button type="button" role="tab" data-testid="checkins-tab-calendar" className={`checkins-tab ${tab === "calendar" ? "active" : ""}`} aria-selected={tab === "calendar"} onClick={() => setTab("calendar")}>Calendar</button>
        {isAdmin && (
          <button type="button" role="tab" data-testid="checkins-tab-blockouts" className={`checkins-tab ${tab === "blockouts" ? "active" : ""}`} aria-selected={tab === "blockouts"} onClick={() => setTab("blockouts")}>Blockouts</button>
        )}
      </div>

      {tab === "calendar" && (
        <div
          className="calendar-preview"
          data-testid="checkins-calendar-preview"
          role="button" tabIndex={0}
          onClick={() => navigate("/check-ins/calendar")}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && navigate("/check-ins/calendar")}
        >
          <div className="calendar-preview-header">
            <div className="calendar-preview-month">{now.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
            <div className="calendar-preview-cta">Tap to open full calendar →</div>
          </div>
          <MonthGrid
            year={year} month={month} itemsByDate={itemsByDate}
            onSelectDate={() => navigate("/check-ins/calendar")}
            compact
          />
        </div>
      )}

      {tab === "blockouts" && isAdmin && (
        <div data-testid="checkins-blockouts-list">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-sub)" }}>
              {upcomingBlockouts.length} upcoming blockout{upcomingBlockouts.length === 1 ? "" : "s"}
            </div>
            <button className="btn btn-gold btn-sm" data-testid="checkins-new-blockout" onClick={() => setShowBlockoutForm(true)}>+ New Blockout</button>
          </div>
          {upcomingBlockouts.length === 0 ? (
            <div className="widget-empty" style={{ background: "var(--kc-elevated)", borderRadius: 12, border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)" }}>
              <div className="we-icon"><CustomIcon name="warning" size={30} /></div>No upcoming blockouts. Use the dropdown menu or the button above to add one.
            </div>
          ) : (
            <div>
              {upcomingBlockouts.map(b => {
                const s = new Date(b.startDate);
                const appliesLabel = (b.appliesTo === "all" || (Array.isArray(b.appliesTo) && b.appliesTo.includes("all")))
                  ? "All users"
                  : `${(Array.isArray(b.appliesTo) ? b.appliesTo.length : 0)} user${(Array.isArray(b.appliesTo) ? b.appliesTo.length : 0) === 1 ? "" : "s"}`;
                return (
                  <div key={b.id} className="blockout-row" data-testid={`blockout-row-${b.id}`}>
                    <div className="bo-date-box">
                      <div className="bd-month">{s.toLocaleDateString("en-US",{month:"short"})}</div>
                      <div className="bd-day">{s.getDate()}</div>
                    </div>
                    <div className="bo-main">
                      <div className="bo-name">{b.name || "Blockout"}</div>
                      <div className="bo-meta">
                        {formatDate(b.startDate)}{b.endDate ? ` → ${formatDate(b.endDate)}` : ""} · {b.allDay ? "All day" : "Timed"}
                      </div>
                      {b.description && <div className="bo-meta" style={{ marginTop: 4 }}>{b.description}</div>}
                    </div>
                    <div className="bo-applies">{appliesLabel}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showBlockoutForm && (
        <BlockoutFormModal
          currentUser={currentUser}
          users={users}
          blockouts={blockouts}
          setBlockouts={setBlockouts}
          persist={persist}
          addAudit={addAudit}
          addNotification={addNotification}
          onClose={() => setShowBlockoutForm(false)}
          onSaved={() => { setShowBlockoutForm(false); setTab("blockouts"); }}
        />
      )}
    </div>
  );
}


// FullCalendarPage + BlockoutFormModal extracted to /components/calendar/
// (see FullCalendarPageRoute.jsx and BlockoutFormModal.jsx).


// ============================================================
// SwipeableNotif — swipe-left (touch + pointer) reveals red zone,
// release past threshold → onDelete(). Hover shows a desktop × button.
// ============================================================
// SwipeableNotif lives in ./SwipeableNotif.jsx and is now imported below.
// SwipeableNotif lives in ./SwipeableNotif.jsx and is now imported below.
// NOTIF_SECTIONS / NOTIF_OTHER_SECTION / notifSectionFor live in
// ../lib/notifSections.js for reuse by NotifPanel and the Archive page.

// =============================================================
// Appointments role-based entry. Public booking vs admin management split:
//   • team_member  → redirect to /appointments/booking (public form)
//   • pastor       → render AppointmentsPage (their own management view)
//   • SA / LA      → render chooser modal
// =============================================================
function AppointmentsEntry({ currentUser, pageProps }) {
  const navE = useNavigate();
  const role = currentUser?.role;
  useEffect(() => {
    if (role === "team_member") navE("/appointments/booking", { replace: true });
  }, [role, navE]);

  if (role === "pastor")        return <AppointmentsPage {...pageProps}/>;
  if (role === "team_member")   return null; // redirecting

  // SA / LA chooser modal — clickable overlay returns to dashboard
  return (
    <div data-testid="appointments-chooser-overlay" onClick={() => navE("/dashboard")} style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", display:"flex",
      alignItems:"center", justifyContent:"center", zIndex:80, padding:20,
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"var(--kc-surface)", border:"1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
        borderRadius:14, padding:"28px 24px", maxWidth:420, width:"100%",
        boxShadow:"0 14px 40px rgba(0,0,0,0.6)", textAlign:"center",
      }}>
        <div style={{fontSize:34, marginBottom:8, display: "inline-flex"}}><CustomIcon name="calendar" size={34} /></div>
        <div data-testid="appointments-chooser-title" style={{fontFamily:"'Cinzel',serif", fontSize:18, fontWeight:700, color:"var(--kc-accent)", letterSpacing:"0.08em", textTransform:"uppercase"}}>
          Appointments
        </div>
        <div style={{fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"var(--kc-sub)", marginTop:6, marginBottom:22, lineHeight:1.5}}>
          Choose how you'd like to use Appointments today.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
          <button
            data-testid="appointments-chooser-book"
            className="btn btn-gold"
            onClick={() => navE("/appointments/booking")}
            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontWeight: 700, letterSpacing: "0.05em", minHeight: 44 }}
          >
            <CustomIcon name="bible" size={14} /> Book an Appointment with a Pastor
          </button>
          <button
            data-testid="appointments-chooser-manage"
            className="btn btn-gold"
            onClick={() => navE("/appointments/manage", { replace: true })}
            style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontWeight: 700, letterSpacing: "0.05em", minHeight: 44 }}
          >
            <CustomIcon name="tools" size={14} /> Manage Administrative Tasks
          </button>
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => navE("/dashboard")} style={{marginTop:16}}>Cancel</button>
      </div>
    </div>
  );
}

export default function App() {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const { theme, setTheme, toggle: toggleTheme } = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  // Mirror the current user into the viewerContext singleton so cross-cutting
  // UI (e.g. Avatar rings) can react to role without prop-drilling.
  useEffect(() => { setViewer(currentUser); }, [currentUser]);
  // Mirror the members list into the membersContext singleton so any Avatar
  // in the tree can auto-resolve status rings via memberId or name.
  const [members, setMembers] = useState([]);
  useEffect(() => { setMembersContext(members); }, [members]);
  const reactNav0 = useNavigate();
  const locationTop = useLocation();
  // CRITICAL: initialize ALL list/object state to empty. Never seed from
  // SEED_* constants — that risks polluting the backend if a save fires
  // before loadData resolves. Real data is loaded from the API via loadData.
  const [users, setUsers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [services, setServices] = useState([]);
  const [events, setEvents] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [audit, setAudit] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [dashboardLayouts, setDashboardLayouts] = useState({});
  const [blockouts, setBlockouts] = useState([]);
  // Iter 84 — flips true after the initial parallel data fetch resolves so
  // list pages (Members / Departments / etc.) can render a loading skeleton
  // instead of the "No members yet" empty state during first paint.
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  // Profile dropdown anchored on the topbar avatar (Sign Out lives here now).
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // Per-section collapse state for the notification bell dropdown.
  // Sections with 0 items are hidden entirely; user can collapse any section
  // independently. State is in-memory only — resets on reload (intentional:
  // the bell expands fresh so users always see new notifs).
  const [collapsedNotifSections, setCollapsedNotifSections] = useState({});
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [sessionWarning, setSessionWarning] = useState(false);
  const SESSION_TIMEOUT = 30 * 60 * 1000;
  const WARNING_TIME = 2 * 60 * 1000;

  // -------- Sidebar mode (Phase-2 nav rebuild) ----------------------------
  // Modes: "expanded" (240 px), "collapsed" (64 px icons-only), "glass"
  // (0 px push, 4 px hover strip reveals a frosted overlay panel).
  // Fresh page load always starts in "expanded"; a 1000 ms timer then
  // auto-collapses UNLESS the cursor is over the sidebar. Cursor enter
  // resets the timer, cursor leave restarts it. Not persisted between
  // sessions.
  const [sidebarMode, setSidebarMode] = useState("expanded");
  const cycleSidebarMode = useCallback(() => {
    setSidebarMode((m) => (m === "expanded" ? "collapsed" : m === "collapsed" ? "glass" : "expanded"));
  }, []);
  // ------------------------------------------------------------------------

  const reactNav = useNavigate();
  const location = useLocation();
  // page id derived from URL for sidebar highlight + topbar title
  const activeNavItem = useMemo(() => {
    const path = location.pathname;
    // pick the longest matching nav path
    return [...NAV_ITEMS].sort((a,b)=>b.path.length-a.path.length).find(i => path === i.path || path.startsWith(i.path + "/"));
  }, [location.pathname]);

  const navigate = useCallback((target) => {
    if (typeof target === "string") reactNav(target);
  }, [reactNav]);

  useEffect(() => {
    (async () => {
      // Restore session if token present
      const token = getToken();
      if (token) {
        const u = await apiMe();
        if (u) {
          setCurrentUser(u);
          syncThemeFromUser(u, setTheme);
        } else {
          setToken(null);
        }
      }
      setLoaded(true);
    })();
  }, []);

  // Load all data from backend after auth
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      // CRITICAL: pass [] as fallback so a transient backend error or
      // empty key never injects seed data into local state. SEED_* constants
      // are NEVER referenced from the runtime data flow.
      const [u,m,f,s,ev,fu,at,au,notif,tk,ap,dl,bo] = await Promise.all([
        loadData("kc_users",[]), loadData("kc_members",[]),
        loadData("kc_families",[]), loadData("kc_services",[]),
        loadData("kc_events",[]), loadData("kc_followups",[]),
        loadData("kc_attendance",[]), loadData("kc_audit",[]),
        loadData("kc_notifications",[]),
        loadData("kc_tasks",[]), loadData("kc_appointments",[]),
        loadData("kc_dashboard_layouts",{}),
        loadData("kc_blockouts",[]),
      ]);
      // The kc_users mirror is server-managed; client-side seed migration
      // no longer needed (backend's _rebuild_kc_users_mirror() carries isSeed).
      // Dedupe by id — legacy seed can occasionally emit duplicate user rows
      // (observed with u2), which would trigger React duplicate-key errors on
      // every list render otherwise.
      const dedupeById = (arr) => {
        if (!Array.isArray(arr)) return [];
        const seen = new Set();
        const out = [];
        for (const item of arr) {
          const id = item?.id;
          if (id && seen.has(id)) continue;
          if (id) seen.add(id);
          out.push(item);
        }
        return out;
      };
      setUsers(dedupeById(u)); setMembers(m); setFamilies(f); setServices(s); setEvents(ev);
      setFollowups(fu); setAttendance(at); setAudit(au); setNotifications(notif);
      setTasks(Array.isArray(tk) ? tk : []); setAppointments(Array.isArray(ap) ? ap : []);
      setDashboardLayouts(dl && typeof dl === "object" && !Array.isArray(dl) ? dl : {});
      setBlockouts(Array.isArray(bo) ? bo : []);
      setDataLoaded(true);
    })();
  }, [currentUser]);

  // Session timeout
  useEffect(() => {
    if (!currentUser) return;
    const onActivity = () => { setLastActivity(Date.now()); setSessionWarning(false); };
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("keydown", onActivity);
    window.addEventListener("click", onActivity);
    const timer = setInterval(() => {
      const idle = Date.now() - lastActivity;
      if (idle >= SESSION_TIMEOUT) { setToken(null); setCurrentUser(null); reactNav0("/dashboard"); }
      else if (idle >= SESSION_TIMEOUT - WARNING_TIME) setSessionWarning(true);
    }, 10000);
    return () => { clearInterval(timer); window.removeEventListener("mousemove",onActivity); window.removeEventListener("keydown",onActivity); window.removeEventListener("click",onActivity); };
  }, [currentUser, lastActivity]);

  // Birthday notifications check
  useEffect(() => {
    if (!currentUser || !members.length) return;
    const bdays = upcomingBirthdays(members, 7);
    bdays.forEach(m => {
      const key = `bday_${m.id}_${new Date().getFullYear()}`;
      const existing = notifications.find(n => n.id === key);
      if (!existing) {
        const msg = m.daysUntilBirthday === 0
          ? `Today is ${fullName(m)}'s birthday!`
          : `Birthday in ${m.daysUntilBirthday} day${m.daysUntilBirthday>1?"s":""}: ${fullName(m)}`;
        const n = { id: key, text: msg, time: "Auto-generated", read: false, type: "birthday", ref: { memberId: m.id } };
        setNotifications(prev => { const updated = [n, ...prev.filter(x=>x.id!==key)]; saveData("kc_notifications", updated); return updated; });
      }
    });
    // New-visitor auto-notifications (last 7 days)
    const isAdmin = currentUser.role === "superadmin" || currentUser.role === "limited_admin";
    if (isAdmin) {
      const recentVisitors = members.filter(m => {
        const isVisitor = m.memberStatus === "Guest" || (m.tags||[]).includes("Visitor");
        if (!isVisitor || !m.joinedDate) return false;
        const days = Math.floor((Date.now() - new Date(m.joinedDate).getTime()) / 86400000);
        return days >= 0 && days <= 7;
      });
      recentVisitors.forEach(v => {
        const key = `visitor_${v.id}`;
        const existing = notifications.find(n => n.id === key);
        if (!existing) {
          const n = { id: key, text: `New visitor: ${fullName(v)} submitted the welcome form`, time: formatDate(v.joinedDate), read: false, type: "visitor", ref: { memberId: v.id } };
          setNotifications(prev => { const updated = [n, ...prev.filter(x=>x.id!==key)]; saveData("kc_notifications", updated); return updated; });
        }
      });
    }
  }, [currentUser, members]);

  const persist = useCallback(async (key, val) => { await saveData(key, val); }, []);
  const addAudit = (userId, userName, action, detail) => {
    const entry = { id: uid(), userId, userName, action, detail, timestamp: new Date().toISOString() };
    // Optimistic local update + server-side append (no bulk PUT → no 409 shrinkage).
    setAudit(a => [entry, ...a]);
    (async () => {
      try {
        await kcFetch(`${API_BASE}/audit`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ actor: userId, actorName: userName, action, detail }),
        });
      } catch { /* non-critical; local state already updated */ }
    })();
  };
  const addNotification = (text, type="general", ref=null, audience="all") => {
    const n = { id:uid(), text, time:"Just now", read:[], type, ref, audience };
    setNotifications(prev => { const updated=[n,...prev]; persist("kc_notifications",updated); return updated; });
  };
  const markNotifRead = (id) => {
    setNotifications(prev => {
      const updated = prev.map(n => {
        if (n.id !== id) return n;
        const readList = Array.isArray(n.read) ? n.read : (n.read ? ["__legacy__"] : []);
        if (readList.includes(currentUser.id)) return n;
        return { ...n, read: [...readList, currentUser.id] };
      });
      persist("kc_notifications", updated);
      return updated;
    });
  };
  const handleNotifClick = (n) => {
    markNotifRead(n.id);
    setShowNotifs(false);
    const ref = n.ref || {};
    if (n.type === "birthday" && ref.memberId) reactNav0(`/members/${ref.memberId}`);
    else if (n.type === "followup" && ref.followupId) reactNav0(`/follow-ups/${ref.followupId}`);
    else if (n.type === "followup_close_request") reactNav0("/follow-ups");
    else if (n.type === "followup_close_approved" || n.type === "followup_close_rejected") reactNav0("/follow-ups");
    else if (n.type === "assignment_added" || n.type === "assignment_removed" || n.type === "assignment_changed" || n.type === "assignment_prewarn" || n.type === "assignment_expired") reactNav0("/departments");
    else if (n.type === "attendance") reactNav0("/attendance");
    else if (n.type === "blockout") reactNav0("/check-ins");
    else if (n.type === "service") {
      // Calendar service notifications open the services management page.
      if (ref.serviceId) reactNav0(`/admin/services?focus=${ref.serviceId}`);
      else reactNav0("/admin/services");
    }
    else if (n.type === "event") {
      if (ref.eventId) reactNav0(`/events?focus=${ref.eventId}`);
      else reactNav0("/events");
    }
    else if (n.type === "appointment") {
      // Appointment notifications land on the user's Tasks → Appointments tab.
      if (ref.appointmentId) reactNav0(`/appointments?focus=${ref.appointmentId}`);
      else reactNav0("/tasks");
    }
    else if (n.type === "task" && ref.taskId) reactNav0("/tasks");
    else if (n.type === "visitor" && ref.memberId) reactNav0(`/members/${ref.memberId}`);
    else if (n.type === "department_announcement" || n.type === "announcement_reminder") {
      if (ref.departmentId) reactNav0(`/departments/${ref.departmentId}`);
      else reactNav0("/departments");
    }
    else if (n.type === "donation_listing" || n.type === "donation_intake") {
      if (ref.intakeId) reactNav0(`/departments`); // donations dept opens via the departments grid
      else reactNav0("/departments");
    }
    else if (n.type === "override_request") reactNav0("/departments");
    else if (n.type === "override_approved" || n.type === "override_denied" || n.type === "priority_revert") {
      if (ref.departmentId) reactNav0(`/departments/${ref.departmentId}`);
      else reactNav0("/departments");
    }
    else if (ref.memberId) reactNav0(`/members/${ref.memberId}`);
  };

  // Override request inline approve / deny — closes the notif as read after action.
  const decideOverrideRequest = async (n, decision) => {
    const ref = n.ref || {};
    if (!ref.requestId) return;
    try {
      const mod = await import("./departments/api");
      await mod.apiDecideOverrideRequest(ref.requestId, decision);
      markNotifRead(n.id);
    } catch (e) {
      alert(e.message || "Could not record decision");
    }
  };

  // ----- Swipe-to-delete + Clear All + Undo (3s window) -----
  const [pendingDelete, setPendingDelete] = useState(null); // { items, timer }
  const pendingDeleteRef = useRef(null);
  useEffect(() => { pendingDeleteRef.current = pendingDelete; }, [pendingDelete]);

  const commitDeletion = () => {
    // Called when undo window expires — clear pending so notifs stay deleted
    setPendingDelete(null);
  };
  const queueDeletion = (notifsToDelete) => {
    if (!notifsToDelete || notifsToDelete.length === 0) return;
    const ids = new Set(notifsToDelete.map(n => n.id));
    setNotifications(prev => {
      const remaining = prev.filter(n => !ids.has(n.id));
      persist("kc_notifications", remaining);
      return remaining;
    });
    // Cancel any prior pending so the new one wins
    if (pendingDeleteRef.current?.timer) clearTimeout(pendingDeleteRef.current.timer);
    const timer = setTimeout(commitDeletion, 3000);
    setPendingDelete({ items: notifsToDelete, timer });
  };
  const undoDelete = () => {
    const pd = pendingDeleteRef.current;
    if (!pd) return;
    clearTimeout(pd.timer);
    setNotifications(prev => {
      // Merge restored items in, dedupe by id, then sort newest-first if possible
      const map = new Map();
      [...pd.items, ...prev].forEach(n => { if (!map.has(n.id)) map.set(n.id, n); });
      const merged = Array.from(map.values());
      // preserve original order: restored items at the positions they originally held by id
      persist("kc_notifications", merged);
      return merged;
    });
    setPendingDelete(null);
  };
  const clearAllNotifications = () => {
    if (notifications.length === 0) return;
    queueDeletion(notifications);
  };
  const unreadCount = notifications.filter(n => {
    if (!n) return false;
    // New shape: read is array of userIds who have read it
    if (Array.isArray(n.read)) return !n.read.includes(currentUser.id);
    // Legacy boolean shape
    return !n.read;
  }).length;

  // Tasks badge: incomplete personal to-dos for the current user
  // + appointments still pending (user is invitee or invited pastor)
  // Recomputes whenever tasks or appointments state changes.
  const taskBadgeCount = useMemo(() => {
    if (!currentUser) return 0;
    const uid = currentUser.id;
    const incompleteTasks = tasks.filter(t => t && t.userId === uid && !t.done).length;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const pendingAppts = appointments.filter(a => {
      if (!a) return false;
      if (a.status !== "pending") return false;
      if (a.userId !== uid && a.pastorId !== uid) return false;
      if (!a.date) return true;
      return new Date(a.date) >= today;
    }).length;
    return incompleteTasks + pendingAppts;
  }, [currentUser, tasks, appointments]);
  const visibleMembers = (user) => {
    if (!user) return [];
    if (user.role==="superadmin"||user.role==="limited_admin") return members;
    return members.filter(m=>(user.assignedMembers||[]).includes(m.id));
  };

  // Globally sort `members` and `users` alphabetically (Last, First) before
  // distributing them to every page/component. This guarantees that EVERY
  // dropdown, search-result list, or selector that consumes these props
  // shows people in a predictable alphabetical order — no per-component
  // sorts required. `setMembers`/`setUsers` still operate on the raw state.
  // NOTE: These hooks MUST live above any early `return` to comply with
  // the rules of hooks.
  const sortedMembers = useMemo(() => sortMembersByName(members), [members]);
  const sortedUsers = useMemo(
    () => sortUsersByName(users, (u) => members.find(m => m.id === u.memberId)),
    [users, members]
  );

  // Backward compatibility: legacy ?welcome=1 → /welcome
  useEffect(() => {
    const search = new URLSearchParams(locationTop.search);
    if (search.get("welcome") === "1") {
      reactNav0("/welcome", { replace: true });
    }
  }, [locationTop.search, reactNav0]);

  if (!loaded) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--kc-surface)"}}>
      <style>{STYLES}</style>
      <div style={{textAlign:"center"}}>
        {appSettingsLoaded
          ? <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} style={{width:160,height:"auto",marginBottom:16,borderRadius:12}}/>
          : <div aria-hidden="true" style={{width:160,height:160,marginBottom:16}} />}
        <div style={{color:"var(--kc-accent)",fontFamily:"'Cinzel',serif",fontSize:16,letterSpacing:"0.1em",textTransform:"uppercase"}}>{appSettings.appName}</div>
        <div style={{color:"var(--kc-meta)",fontSize:12,marginTop:6,fontFamily:"'Montserrat',sans-serif"}}>Loading...</div>
      </div>
    </div>
  );

  // Public visitor route is always accessible (no auth)
  if (locationTop.pathname === "/welcome") return (
    <>
      <style>{STYLES}</style>
      <VisitorWelcomePage />
    </>
  );

  // Public pastoral-appointments booking page (no auth) — entered via QR
  if (locationTop.pathname === "/appointments/booking") return (
    <PublicAppointmentBookingPage />
  );

  // Public Event registration page (no auth) — entered via per-event QR
  // poster. URL format: /events/:id/register
  {
    const m = /^\/events\/([^/]+)\/register$/.exec(locationTop.pathname);
    if (m) {
      const EventRegisterRoute = PublicEventRegisterPage;
      // Mount router context so useParams works.
      return (
        <Routes>
          <Route path="/events/:eventId/register" element={<EventRegisterRoute/>} />
        </Routes>
      );
    }
  }

  // Public KGC Compassion Food Contribution Registration form — entered via QR
  if (locationTop.pathname === "/donations/food-registration") return (
    <FoodFormPublicPage />
  );

  // Public Donations Listings — community members browse available donated items via QR scan
  if (locationTop.pathname === "/donations/listings") return (
    <PublicDonationsListingsPage />
  );

  if (!currentUser) return (
    <>
      <style>{STYLES}</style>
      <LoginScreen users={users} onLogin={u=>{setCurrentUser(u);syncThemeFromUser(u,setTheme);reactNav0("/dashboard");setLastActivity(Date.now());}} />
    </>
  );

  // After login, /login redirects to the dashboard so direct visits or
  // back-button navigation don't strand the user on a useless page.
  if (locationTop.pathname === "/login") {
    return <Navigate to="/dashboard" replace />;
  }

  const isSuperAdmin = currentUser.role==="superadmin";
  const isLimitedAdmin = currentUser.role==="limited_admin";
  const isPastor = currentUser.role==="pastor";
  const isTeamMember = currentUser.role==="team_member";
  const navGroups = buildNavGroups(currentUser.role).map(g => ({
    ...g,
    items: g.items.map(it => it.id === "followups" ? { ...it, label: appSettings.followUpsLabel } : it),
  }));

  const pageTitle = activeNavItem
    ? (activeNavItem.id === "followups" ? appSettings.followUpsLabel : activeNavItem.label)
    : (location.pathname === "/me" ? "My Profile" : appSettings.appName);
  // Globally sort `members` and `users` alphabetically (Last, First) before
  // distributing them to every page/component. This guarantees that EVERY
  // dropdown, search-result list, or selector that consumes these props
  // shows people in a predictable alphabetical order — no per-component
  // sorts required. `setMembers`/`setUsers` still operate on the raw state.
  const pageProps = { currentUser, isSuperAdmin, isLimitedAdmin, isPastor, isTeamMember, members: sortedMembers, setMembers, families, setFamilies, services, setServices, events, setEvents, followups, setFollowups, attendance, setAttendance, users: sortedUsers, setUsers, audit, departments:[], visibleMembers, addAudit, addNotification, markNotifRead, persist, navigate, dashboardLayouts, setDashboardLayouts, appointments, setAppointments, tasks, setTasks, blockouts, setBlockouts, dataLoaded };

  return (
    <>
      <style>{STYLES}</style>
      {sessionWarning && (
        <div className="session-banner">
          <CustomIcon name="clock" size={16} style={{marginRight:6}} />Your session will expire in 2 minutes due to inactivity. Move your mouse to stay logged in.
        </div>
      )}
      <div
        className="layout"
        data-sidebar-mode={sidebarMode}
        onClick={()=>{ setShowProfileMenu(false); setShowNotifs(false); }}
      >
        {/* Glass mode strip — 4 px vertical bar on left edge that
            reveals the sidebar overlay on hover. Only visible when
            sidebarMode === "glass". Tapping it also cycles the mode. */}
        <div
          className="sidebar-glass-strip"
          data-testid="sidebar-glass-strip"
          aria-hidden={sidebarMode !== "glass"}
          onClick={(e) => { e.stopPropagation(); cycleSidebarMode(); }}
        />
        <aside
          className="sidebar"
          data-testid="app-sidebar"
        >
          <NavLink to="/dashboard" data-testid="sidebar-logo-link" className="sidebar-logo" style={{textDecoration:"none",color:"inherit",cursor:"pointer"}}>
            {appSettingsLoaded
              ? <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="sidebar-logo-img" />
              : <div className="sidebar-logo-img" aria-hidden="true" style={{ visibility: "hidden" }} />}
            <div className="sidebar-logo-sub sidebar-fade" data-testid="sidebar-tagline">{appSettings.appTagline}</div>
          </NavLink>
          <nav className="sidebar-nav">
            {navGroups.map(g=>(
              <div key={g.section}>
                <div className="sidebar-section sidebar-fade">{g.section}</div>
                {g.items.map(item=>(
                  <NavLink
                    key={item.id}
                    to={item.path}
                    className={({isActive})=>`nav-item ${isActive?"active":""}`}
                    data-tooltip={item.label}
                    data-testid={`sidebar-nav-${item.id}`}
                  >
                    <span className="nav-icon">
                      {item.customIcon ? <CustomIcon name={item.customIcon} size={18} /> : item.icon}
                    </span>
                    <span className="nav-item-label sidebar-fade">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <div className="sidebar-footer">
            <NavLink to="/me" data-testid="sidebar-user-link" className="sidebar-user-chip sidebar-fade" style={{textDecoration:"none"}}>
              <Avatar className="sidebar-avatar" name={currentUser.name} photo={currentUser.photo} memberId={currentUser.memberId}/>
              <div>
                <div className="sidebar-user-name">{currentUser.name}</div>
                <div className="sidebar-user-role">{roleLabel(currentUser.role)}</div>
              </div>
            </NavLink>
            <button
              type="button"
              className="sidebar-logout"
              data-testid="sidebar-logout-btn"
              onClick={() => {
                localStorage.removeItem("kc_token");
                window.location.href = "/login";
              }}
              aria-label="Sign out"
              data-tooltip="Sign out"
            >
              <CustomIcon name="logout" size={16} />
              <span className="nav-item-label sidebar-fade">Sign out</span>
            </button>
          </div>
        </aside>
        {/* Chevron toggle — pinned to the right edge of the sidebar (or
            the glass strip). Cycles expanded → collapsed → glass. */}
        <button
          type="button"
          className="sidebar-toggle"
          data-testid="sidebar-toggle-btn"
          onClick={cycleSidebarMode}
          aria-label={`Sidebar mode: ${sidebarMode}. Click to cycle.`}
          data-mode={sidebarMode}
          title={`Mode: ${sidebarMode}`}
        >
          {sidebarMode === "expanded"
            ? <ChevronLeft size={18} />
            : <ChevronRight size={18} />}
        </button>

        <div className="main">
          <div className="topbar">
            <div className="flex items-center gap-10" style={{flex:1,minWidth:0}}>
              <NavLink to="/dashboard" className="topbar-mobile-logo" data-testid="topbar-logo-link" aria-label="Go to dashboard" style={{textDecoration:"none"}}>
                {appSettingsLoaded
                  ? <img src={resolveLogo(appSettings.appLogo)} alt="" />
                  : <span aria-hidden="true" style={{ display: "inline-block", width: 32, height: 32 }} />}
              </NavLink>
              <div className="topbar-title" data-testid="topbar-title">{pageTitle}</div>
            </div>
            <div className="topbar-right">
              <button
                type="button"
                className="theme-toggle-btn"
                data-testid="topbar-theme-toggle"
                onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? (
                  /* Sun */
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                  </svg>
                ) : (
                  /* Moon */
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
                  </svg>
                )}
              </button>
              <button className="notif-btn" data-testid="topbar-notif-btn" onClick={e=>{e.stopPropagation();setShowNotifs(s=>!s);setShowProfileMenu(false)}} aria-label="Notifications">
                <CustomIcon name="bell" size={22} />{unreadCount>0&&<span className="notif-badge" aria-hidden="true"/>}
              </button>
              <button
                data-testid="topbar-profile-avatar"
                onClick={e=>{e.stopPropagation();setShowProfileMenu(s=>!s);setShowNotifs(false)}}
                style={{background:"transparent",border:"none",padding:0,cursor:"pointer",borderRadius:"50%",overflow:"hidden",width:36,height:36,boxShadow:"0 0 0 2px color-mix(in srgb, var(--kc-accent) 45%, transparent)"}}
                aria-label="Open profile menu"
              >
                <Avatar name={currentUser.name} photo={currentUser.photo} memberId={currentUser.memberId} style={{width:36,height:36,fontSize:13}}/>
              </button>
              {showProfileMenu && (
                <div
                  data-testid="topbar-profile-menu"
                  onClick={e=>e.stopPropagation()}
                  style={{
                    position:"absolute", top:48, right:0, minWidth:220,
                    background:"var(--kc-surface)", border:"1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
                    borderRadius:12, padding:"14px 16px", zIndex:60,
                    boxShadow:"0 10px 30px rgba(0,0,0,0.5)",
                  }}
                >
                  <div style={{fontFamily:"'Cinzel',serif",fontSize:14,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--kc-text)",marginBottom:2}}>{currentUser.name}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"color-mix(in srgb, var(--kc-accent) 85%, transparent)",marginBottom:12}}>{roleLabel(currentUser.role)}</div>
                  <button
                    className="btn btn-outline btn-sm w-full"
                    data-testid="logout-btn"
                    onClick={() => {
                      // Full reload to /login avoids re-rendering the app
                      // tree with currentUser=null (would crash filters that
                      // read currentUser.id). localStorage.removeItem clears
                      // the token so the reloaded app boots into LoginScreen.
                      localStorage.removeItem("kc_token");
                      window.location.href = "/login";
                    }}
                    style={{justifyContent:"center",display:"inline-flex",alignItems:"center",gap:8}}
                  ><CustomIcon name="logout" size={16} /> Sign Out</button>
                </div>
              )}
              {showNotifs && (
                <NotifPanel
                  notifications={notifications}
                  currentUser={currentUser}
                  clearAllNotifications={clearAllNotifications}
                  collapsedNotifSections={collapsedNotifSections}
                  setCollapsedNotifSections={setCollapsedNotifSections}
                  handleNotifClick={handleNotifClick}
                  decideOverrideRequest={decideOverrideRequest}
                  queueDeletion={queueDeletion}
                  pendingDelete={pendingDelete}
                  undoDelete={undoDelete}
                  onClose={() => setShowNotifs(false)}
                  navigate={reactNav0}
                />
              )}
            </div>
          </div>

          <div className="content" onClick={()=>showNotifs&&setShowNotifs(false)}>
            <AccessDeniedBanner/>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/internal/card-preview" element={<CardPreview/>} />
              <Route path="/dashboard" element={<DashboardPage {...pageProps} notifications={notifications} members={members}/>} />
              <Route path="/me" element={<MyProfilePage currentUser={currentUser} setCurrentUser={setCurrentUser} users={sortedUsers} setUsers={setUsers} members={sortedMembers} setMembers={setMembers} persist={persist}/>} />
              <Route path="/members" element={<MembersPage {...pageProps}/>} />
              <Route path="/members/:id" element={<MembersPage {...pageProps}/>} />
              <Route path="/families" element={<FamiliesPage {...pageProps}/>} />
              <Route path="/families/:id" element={<FamiliesPage {...pageProps}/>} />
              <Route path="/check-ins" element={<CheckinsLandingPage {...pageProps}/>} />
              <Route path="/check-ins/calendar" element={<FullCalendarPageRoute {...pageProps}/>} />
              <Route path="/calendar" element={<Navigate to="/check-ins/calendar" replace />} />
              <Route path="/attendance" element={<AttendancePage {...pageProps}/>} />
              <Route path="/follow-ups" element={<Navigate to="/followuptree" replace/>} />
              <Route path="/follow-ups/:id" element={<Navigate to="/followuptree" replace/>} />
              <Route path="/follow-ups-archive" element={<LogEntryPage currentUser={currentUser}/>} />
              <Route path="/followuptree" element={<FollowUpTreePage {...pageProps}/>} />
              <Route path="/events" element={<EventsPage {...pageProps}/>} />
              <Route path="/birthdays" element={<BirthdaysPage members={members}/>} />
              <Route path="/birthdays-this-week" element={<BirthdaysThisWeekPage/>} />
              <Route path="/my-attendance" element={<MyAttendancePage {...pageProps}/>} />
              <Route path="/reports/analytics" element={<RequireRole allow={["superadmin","limited_admin","pastor"]} role={currentUser.role}><AnalyticsPage {...pageProps}/></RequireRole>} />
              <Route path="/reports/baptism" element={<RequireRole allow={["superadmin","limited_admin"]} role={currentUser.role}><BaptismReport members={members}/></RequireRole>} />
              <Route path="/reports/new-members" element={<RequireRole allow={["superadmin","limited_admin"]} role={currentUser.role}><NewMembersReport members={members}/></RequireRole>} />
              <Route path="/reports/family-growth" element={<RequireRole allow={["superadmin","limited_admin"]} role={currentUser.role}><FamilyGrowthReport members={members} families={families}/></RequireRole>} />
              <Route path="/audit" element={<RequireRole allow={["superadmin"]} role={currentUser.role}><AuditPage audit={audit}/></RequireRole>} />
              <Route path="/notifications" element={<NotificationsArchivePage notifications={notifications} currentUser={currentUser} handleNotifClick={handleNotifClick} queueDeletion={queueDeletion}/>} />
              <Route path="/admin/users" element={<RequireRole allow={["superadmin"]} role={currentUser.role}><UsersPage {...pageProps}/></RequireRole>} />
              <Route path="/admin/services" element={<ServicesPage {...pageProps}/>} />
              <Route path="/admin/bulk-import" element={<RequireRole allow={["superadmin"]} role={currentUser.role}><BulkImportPage {...pageProps}/></RequireRole>} />
              <Route path="/admin/settings" element={<RequireRole allow={["superadmin"]} role={currentUser.role}><SettingsPage {...pageProps}/></RequireRole>} />
              <Route path="/admin/qr-poster" element={<QRPosterPage currentUser={currentUser} isSuperAdmin={isSuperAdmin} events={events}/>} />
              <Route path="/browse" element={<BrowsePage currentUser={currentUser}/>} />
              <Route path="/tasks" element={<TasksPage {...pageProps}/>} />
              <Route path="/appointments" element={<AppointmentsEntry currentUser={currentUser} pageProps={pageProps}/>} />
              <Route path="/appointments/manage" element={<AppointmentsPage {...pageProps}/>} />
              <Route path="/departments" element={<DepartmentsPage {...pageProps}/>} />
              <Route path="/donations/:id" element={<DonationsPage {...pageProps}/>} />
              <Route path="/donations" element={<DonationsPage {...pageProps}/>} />
              <Route path="/departments/:id" element={<DepartmentDetailPage {...pageProps}/>} />
              <Route path="/giving" element={<GivingPage currentUser={currentUser} setCurrentUser={setCurrentUser}/>} />
              <Route path="/finance" element={<FinancePage currentUser={currentUser} members={members} appSettings={appSettings} navigate={reactNav0}/>} />
              <Route path="/dev/components" element={<ComponentPreviewPage/>} />
              <Route path="/admin/pastors-directory" element={<RequireRole allow={["superadmin","limited_admin"]} role={currentUser.role}><PastorsDirectoryPage/></RequireRole>} />
              <Route path="*" element={<NotFoundPage/>} />
            </Routes>
          </div>
        </div>
        <BottomNavBar taskBadgeCount={taskBadgeCount} currentUser={currentUser} navItems={NAV_ITEMS} />
        <DarkModePopup currentUser={currentUser} />
      </div>
    </>
  );
}

// ============================================================
// LOGIN
// ============================================================
function LoginScreen({ users, onLogin }) {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleLogin = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const { token, user } = await apiLogin(username, password);
      setToken(token);
      onLogin(user);
    } catch (e) {
      setError(e.message || "Invalid username or password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="login-bg">
      <div className="login-card">
        {appSettingsLoaded ? (
          <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="login-logo-img" />
        ) : (
          <div className="login-logo-img" aria-hidden="true" data-testid="login-logo-placeholder" style={{ visibility: "hidden" }} />
        )}
        <div className="login-title" data-testid="login-app-name">{appSettings.appName}</div>
        <div className="login-tagline" data-testid="login-app-tagline">{appSettings.appTagline}</div>

        {error&&<div className="login-error">{error}</div>}

        <label className="login-label">Username</label>
        <input className="login-input" data-testid="login-username" value={username} onChange={e=>setUsername(e.target.value)} placeholder="Enter your username" onKeyDown={e=>e.key==="Enter"&&handleLogin()}/>
        <div data-testid="login-username-hint" style={{marginTop:-10,marginBottom:14,fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"color-mix(in srgb, var(--kc-accent) 65%, transparent)",letterSpacing:"0.02em"}}>Username is not case-sensitive</div>
        <label className="login-label">Password</label>
        <div className="password-field-wrap login-pw">
          <input className="login-input" data-testid="login-password" type={showPassword?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)} placeholder="Enter your password" onKeyDown={e=>e.key==="Enter"&&handleLogin()} style={{marginBottom:0}}/>
          <button type="button" className="password-toggle" data-testid="login-password-toggle" onClick={()=>setShowPassword(s=>!s)} aria-label={showPassword?"Hide password":"Show password"} tabIndex={-1}><CustomIcon name={showPassword?"eye-closed":"eye-open"} size={18} /></button>
        </div>
        <div style={{height:14}}/>
        <button className="login-btn" data-testid="login-submit" onClick={handleLogin} disabled={busy}>{busy?"Signing in…":"Sign In"}</button>
        <div style={{marginTop:14,padding:"10px 12px",background:"color-mix(in srgb, var(--kc-accent) 6%, transparent)",borderRadius:8,border:"1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)",fontFamily:"'Montserrat',sans-serif",fontSize:11,color:"var(--kc-sub)",textAlign:"center",lineHeight:1.5}}>
          Forgot your password? Please contact your Super Admin —<br/>they can reset it for you in <strong style={{color:"var(--kc-accent)"}}>Users &amp; Roles</strong>.
        </div>
        <div style={{marginTop:14,textAlign:"center"}}>
          <a href="/welcome" style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:"var(--kc-accent)",textDecoration:"underline"}}>First time here? Introduce yourself →</a>
        </div>
      </div>
    </div>
  );
}



// ============================================================
// DEPARTMENTS — Phase 1 (CRUD + Leader + Members + Positions)
// ============================================================

function DepartmentsPage({ currentUser, isSuperAdmin, isLimitedAdmin, members, users = [], navigate }) {
  const isAdmin = isSuperAdmin || isLimitedAdmin;
  const [depts, setDepts] = useState([]);
  const [counts, setCounts] = useState({}); // {deptId: {users, positions}}
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deptOrder, setDeptOrder] = useState([]); // user-specific ordered IDs
  const [dragIdx, setDragIdx] = useState(null);
  const orderStorageKey = currentUser?.id ? `kc_dept_order_${currentUser.id}` : null;

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await apiListDepartments();
      setDepts(Array.isArray(list) ? list : []);
      // Counts: users from dept.users[], positions via parallel calls
      const c = {};
      await Promise.all((Array.isArray(list) ? list : []).map(async d => {
        const u = Array.isArray(d.users) ? d.users.length : 0;
        const positions = await apiListPositions(d.id).catch(() => []);
        c[d.id] = { users: u, positions: positions.length };
      }));
      setCounts(c);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Load per-user department order from backend.
  useEffect(() => {
    if (!orderStorageKey) return;
    let alive = true;
    _apiGet(`/storage/${orderStorageKey}`)
      .then(res => {
        if (!alive) return;
        // Backend returns { key, value } — the ordered array lives in `value`.
        const arr = Array.isArray(res?.value) ? res.value : (Array.isArray(res) ? res : []);
        if (arr.length > 0) setDeptOrder(arr);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [orderStorageKey]);

  const persistOrder = async (nextOrder) => {
    if (!orderStorageKey) return;
    // Backend StoragePut expects a `{ value }` envelope.
    try { await _apiPut(`/storage/${orderStorageKey}`, { value: nextOrder }); } catch { /* silent */ }
  };

  // Sorted depts: honour deptOrder for known IDs, then alphabetical fallback
  // for depts not yet in the saved order.
  const orderedDepts = useMemo(() => {
    const byId = new Map(depts.map(d => [d.id, d]));
    const chosen = [];
    const seen = new Set();
    deptOrder.forEach(id => { const d = byId.get(id); if (d) { chosen.push(d); seen.add(id); } });
    const rest = depts
      .filter(d => !seen.has(d.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return [...chosen, ...rest];
  }, [depts, deptOrder]);

  const autoScrollRef = useRef({ raf: 0, dir: 0 });
  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current.raf) {
      cancelAnimationFrame(autoScrollRef.current.raf);
      autoScrollRef.current.raf = 0;
    }
    autoScrollRef.current.dir = 0;
  }, []);
  const startAutoScroll = useCallback((dir) => {
    if (autoScrollRef.current.dir === dir) return;
    stopAutoScroll();
    autoScrollRef.current.dir = dir;
    const step = () => {
      if (autoScrollRef.current.dir === 0) return;
      window.scrollBy(0, autoScrollRef.current.dir * 4);
      autoScrollRef.current.raf = requestAnimationFrame(step);
    };
    autoScrollRef.current.raf = requestAnimationFrame(step);
  }, [stopAutoScroll]);
  const handleDragStart = (idx) => (e) => {
    setDragIdx(idx);
    if (e.dataTransfer) { try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(idx)); } catch { /* noop */ } }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    // Auto-scroll when the drag pointer nears the viewport edges. Uses a
    // constant 4px per frame in whichever direction the user is heading.
    const y = e.clientY;
    const vh = window.innerHeight;
    if (y > 0 && y < 80) startAutoScroll(-1);
    else if (y > vh - 80 && y < vh) startAutoScroll(1);
    else stopAutoScroll();
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    stopAutoScroll();
    if (dragIdx == null || dragIdx === idx) { setDragIdx(null); return; }
    const next = orderedDepts.map(d => d.id);
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDeptOrder(next);
    persistOrder(next);
    setDragIdx(null);
  };
  const handleDragEnd = () => { stopAutoScroll(); setDragIdx(null); };
  useEffect(() => () => stopAutoScroll(), [stopAutoScroll]);
  const resetOrder = () => {
    setDeptOrder([]);
    persistOrder([]);
  };

  // Resolve a userId to a display object — linked member name/photo if available,
  // otherwise the user's own username + photo + initials.
  const userIdToDisplay = (uid) => {
    const u = (users || []).find(x => x.id === uid);
    if (!u) return null;
    const linkedMember = u.memberId ? members.find(m => m.id === u.memberId) : null;
    return {
      id: uid,
      username: u.username,
      name: linkedMember ? fullName(linkedMember) : (u.name || u.username),
      photo: (linkedMember && linkedMember.photo) || u.photo || null,
    };
  };

  const save = async (data) => {
    try {
      if (editDept) {
        const updated = await apiUpdateDepartment(editDept.id, data);
        setDepts(prev => prev.map(d => d.id === editDept.id ? updated : d));
      } else {
        const created = await apiCreateDepartment(data);
        setDepts(prev => [...prev, created]);
      }
      setEditDept(null);
      reload();
      return { ok: true };
    } catch (e) { return { ok: false, error: e.message || "Could not save" }; }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This will remove the department from all linked member profiles and delete its positions.`)) return;
    try {
      await apiDeleteDepartment(d.id);
      setDepts(prev => prev.filter(x => x.id !== d.id));
      reload();
    } catch (e) { alert(e.message || "Could not delete"); }
  };

  return (
    <div data-testid="departments-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Departments</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {deptOrder.length > 0 && (
            <button className="btn btn-sm btn-outline" data-testid="dept-reset-order-btn" onClick={resetOrder}>Reset Order</button>
          )}
          {isAdmin && (
            <button className="btn btn-gold" data-testid="dept-new-btn" onClick={() => { setEditDept(null); setShowForm(true); }}>+ New Department</button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="dept-grid" data-testid="dept-grid-loading" aria-busy="true">
          {[0,1,2,3].map(i => (
            <div key={i} className="dept-card" style={{ opacity: 0.6 }}>
              <div style={{ height: 160, background: "color-mix(in srgb, var(--kc-text) 8%, transparent)", borderTopLeftRadius: "var(--kc-radius)", borderTopRightRadius: "var(--kc-radius)" }} />
              <div className="listing-card-body">
                <div style={{ height: 14, width: "60%", background: "color-mix(in srgb, var(--kc-text) 10%, transparent)", borderRadius: 4, marginBottom: 8 }} />
                <div style={{ height: 12, width: "40%", background: "color-mix(in srgb, var(--kc-text) 8%, transparent)", borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
      ) : depts.length === 0 ? (
        <div className="widget-empty-add">
          <div className="wea-icon"><CustomIcon name="tools" size={40} /></div>
          <div className="wea-title">No departments yet</div>
          <div className="wea-sub">{isAdmin ? "Create the first department to organise your ministry teams." : "An admin can create departments here."}</div>
          {isAdmin && <button className="btn btn-gold" onClick={() => { setEditDept(null); setShowForm(true); }}>+ New Department</button>}
        </div>
      ) : (
        <div className="dept-grid" data-testid="dept-grid">
          {/* Special Follow-Ups card — pinned at the start of the grid, opens
              the existing Follow-Ups page. Visible to all roles; backend
              still enforces access control inside the FU module. */}
          <FollowUpsDeptCard navigate={navigate} />
          {orderedDepts.map((d, idx) => {
            const c = counts[d.id] || { users: 0, positions: 0 };
            const leader = d.leaderUserId ? userIdToDisplay(d.leaderUserId) : null;
            const targetPath = `/departments/${d.id}`;
            // Iter 81: Amber variant for care/pastoral/visitor-facing depts;
            // Blue for informational/administrative (default).
            const nameLc = String(d.name || "").toLowerCase();
            const cardVariant = (d.isDonations || /care|pastoral|visitor|follow|welcome|hospital/.test(nameLc))
              ? "amber"
              : "blue";
            const iconName = d.isFinance ? "coin" : d.isDonations ? "care-heart" : "tools";
            return (
              <div
                key={d.id}
                className={`dept-card card-${cardVariant} ${d.isDonations ? "dept-card-donations" : ""} ${d.isFinance ? "dept-card-finance" : ""}`}
                data-testid={`dept-card-${d.id}`}
                draggable
                onDragStart={handleDragStart(idx)}
                onDragOver={handleDragOver}
                onDrop={handleDrop(idx)}
                onDragEnd={handleDragEnd}
                onClick={() => navigate(targetPath)}
                role="link" tabIndex={0}
                onKeyDown={e => (e.key === "Enter" || e.key === " ") && navigate(targetPath)}
                style={{ opacity: dragIdx === idx ? 0.5 : 1, cursor: "grab", position: "relative" }}
              >
                {/* ZONE 1 — Cover image area (reuses shared CoverImage primitive) */}
                <div style={{ position: "relative" }}>
                  <CoverImage
                    value={d.coverImage || ""}
                    title=""
                    subtitle={""}
                    iconName={iconName}
                    overlay={typeof d.overlay === "number" ? d.overlay : 65}
                    textColor={d.textColor || "white"}
                    textEffect={d.textEffect || "shadow"}
                    data-testid={`dept-cover-${d.id}`}
                    style={{ borderRadius: 0, borderLeft: "none", borderRight: "none", borderTop: "none", height: 160, aspectRatio: "auto" }}
                  />
                  {/* Iter 84: only the priority pill lives on the cover now.
                      The dept name has moved to Zone 2 to keep the logo badge
                      from ever occluding it. */}
                  <div
                    className="listing-card-pills"
                    style={{
                      position: "absolute",
                      top: "var(--space-sm)",
                      right: "var(--space-sm)",
                      display: "flex",
                      gap: 6,
                      zIndex: 4,
                      pointerEvents: "none",
                    }}
                  >
                    <span data-testid={`dept-card-priority-badge-${d.id}`} className="listing-card-pill">
                      {((d.effectivePriority || d.priority) || "medium")}
                    </span>
                    {d.overrideActive && (
                      <span data-testid={`dept-card-override-badge-${d.id}`} className="listing-card-pill" title="Priority override active">
                        Override
                      </span>
                    )}
                  </div>
                </div>

                {/* ZONE 2 — Card body */}
                <div className="listing-card-body" style={{ paddingTop: "var(--space-sm)" }}>
                  {/* Logo badge — sits at the top of Zone 2, straddling the Zone 1/2 boundary
                      via `top: -20px` (position rules live in .listing-card-logo-badge). */}
                  {d.logoUrl ? (
                    <div className="listing-card-logo-badge" aria-hidden="true">
                      <img src={d.logoUrl} alt="" />
                    </div>
                  ) : null}
                  {/* Department name — top of Zone 2, sits directly above the badge in DOM
                      order, uses `.text-card-primary` semantics (var(--kc-text)). Left
                      padding clears the 40 px badge overhang on the left. */}
                  <div
                    className="text-card-primary"
                    data-testid={`dept-card-name-${d.id}`}
                    style={{
                      fontFamily: "'Cinzel', serif",
                      fontSize: 15,
                      fontWeight: 700,
                      color: "var(--kc-text)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      lineHeight: 1.2,
                      paddingLeft: d.logoUrl ? 52 : 0,
                      minHeight: d.logoUrl ? 26 : undefined,
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {d.name}
                  </div>

                  {d.description && (
                    <div className="listing-card-desc">
                      {d.description}
                    </div>
                  )}

                  <div className="dept-leader-line">
                    {leader
                      ? <><Avatar name={leader.name} photo={leader.photo} memberId={leader.memberId} style={{ width: 24, height: 24, fontSize: 10 }} /><span className="text-card-secondary">{leader.name}</span></>
                      : <span style={{ color: "var(--kc-meta)", fontStyle: "italic", fontSize: 12 }}>No leader assigned</span>}
                  </div>

                  <div className="dept-stats text-meta" style={{ display: "flex", gap: 6 }}>
                    <span>{c.users} {c.users === 1 ? "user" : "users"}</span>
                    <span>·</span>
                    <span>{c.positions} {c.positions === 1 ? "position" : "positions"}</span>
                  </div>

                  <div className="flex flex-wrap" style={{ gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                    <button className="btn-secondary btn-sm" data-testid={`dept-view-${d.id}`} onClick={() => navigate(targetPath)}>View</button>
                    {isAdmin && <button className="btn-secondary btn-sm" data-testid={`dept-edit-${d.id}`} onClick={() => { setEditDept(d); setShowForm(true); }}>Edit</button>}
                    {isSuperAdmin && (
                      <button
                        type="button"
                        data-testid={`dept-delete-${d.id}`}
                        onClick={() => remove(d)}
                        style={{
                          background: "transparent", border: "none",
                          color: "var(--kc-red)", cursor: "pointer",
                          fontFamily: "'Inter',sans-serif", fontSize: 11,
                          fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                          padding: "6px 10px",
                        }}
                      >Del</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <DepartmentFormModal existing={editDept} members={members} users={users} isSuperAdmin={isSuperAdmin} onSave={save} onClose={() => { setShowForm(false); setEditDept(null); }} />
      )}
    </div>
  );
}






// ---- Widget registry -----------------------------------------------
// Each entry: { id, title, icon, iconBg, description, roles, render(ctx) }
// roles array gates both the picker and the dashboard for that role.

// ============================================================
// BIRTHDAYS PAGE
// ============================================================
function BirthdaysPage({ members }) {
  const [range, setRange] = useState(30);
  const bdays = upcomingBirthdays(members, range);
  return (
    <div>
      <div className="flex items-center justify-between mb-20">
        <div style={{fontSize:13,color:"var(--text3)",fontFamily:"Montserrat,sans-serif"}}>{bdays.length} upcoming birthdays</div>
        <select value={range} onChange={e=>setRange(Number(e.target.value))} style={{padding:"8px 12px",fontSize:13,borderRadius:8,border:"1.5px solid var(--cream2)",background:"var(--cream)",fontFamily:"Montserrat,sans-serif"}}>
          <option value={7}>Next 7 days</option>
          <option value={14}>Next 14 days</option>
          <option value={30}>Next 30 days</option>
          <option value={365}>All this year</option>
        </select>
      </div>
      <div className="card">
        <div className="card-header"><div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="birthday-cake" size={16} /> Upcoming Birthdays</div></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Member</th><th>Date of Birth</th><th>Age Turning</th><th>Days Away</th><th>Department</th></tr></thead>
            <tbody>
              {bdays.map(m=>(
                <tr key={m.id}>
                  <td><div className="flex items-center gap-10"><Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{fontSize:11}}/><span style={{fontWeight:500}}>{fullName(m)}</span></div></td>
                  <td>{formatDate(m.dob)}</td>
                  <td>{age(m.dob)+1}</td>
                  <td>{m.daysUntilBirthday===0?<span className="badge badge-gold" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="party" size={10} /> Today!</span>:<span className="badge badge-charcoal">{m.daysUntilBirthday} days</span>}</td>
                  <td>{m.departments[0]||"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {bdays.length===0&&<div className="empty"><div className="empty-icon"><CustomIcon name="birthday-cake" size={40} /></div><div className="empty-text">No birthdays in this period</div></div>}
        </div>
      </div>
    </div>
  );
}

// FamiliesPage extracted to its feature folder.

// FamilyForm extracted to its feature folder.

// ============================================================
// ATTENDANCE PAGE
// ============================================================

// ============================================================
// EVENTS PAGE
// ============================================================

// ============================================================
// REPORTS PAGE
// ============================================================

// ============================================================
// AUDIT LOG
// ============================================================
function AuditPage({ audit }) {
  const sortedAudit = useMemo(
    () => [...audit].sort((a,b)=>new Date(b.timestamp)-new Date(a.timestamp)),
    [audit]
  );
  return (
    <div className="card">
      <div className="card-header"><div className="card-title">Audit Log ({audit.length} entries)</div></div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Timestamp</th><th>User</th><th>Action</th><th>Detail</th></tr></thead>
          <tbody>
            {sortedAudit.map(a=>(
              <tr key={a.id}><td style={{fontSize:11,color:"var(--text3)",whiteSpace:"nowrap"}}>{new Date(a.timestamp).toLocaleString()}</td><td><span className="badge badge-charcoal">{a.userName}</span></td><td style={{fontSize:13,fontWeight:500}}>{a.action}</td><td style={{fontSize:13,color:"var(--text2)"}}>{a.detail}</td></tr>
            ))}
          </tbody>
        </table>
        {audit.length===0&&<div className="empty"><div className="empty-icon"><CustomIcon name="lock" size={40} /></div><div className="empty-text">No audit entries yet</div></div>}
      </div>
    </div>
  );
}

// UsersPage extracted to its feature folder.

// UserForm extracted to its feature folder.


// BulkImportPage extracted to its feature folder.
